import CreditMeterButton from 'pages/credit-plans/CreditMeterButton';
import React from 'react';
import * as XLSX from 'xlsx';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
  Home,
  ChevronRight,
  Package,
  LayoutDashboard,
  AlertTriangle,
  Activity,
  Flag,
  Truck,
  Bell,
  Plus,
  Upload,
  Download,
  Trash2,
  AlertCircle,
  DollarSign,
  Receipt,
  Sparkles,
  Bot,
  Settings,
  Clock,
  List,
  Info,
  Save,
  Send,
  RefreshCw,
  Check,
  Lock,
  FileStack,
} from './icons';
import type { IconProps } from './icons';
import {
  Button,
  cx,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Input,
} from './ui';
import { formatDate, countryNameFromCode } from './lib/store';
import { WorldMap } from './WorldMap';
import {
  IMPORT_DATABASES,
  IMPORT_PRODUCTS,
  PGA_AGENCIES,
  getEntriesForDatabase,
} from './lib/import-data';
import { KpiTile } from './primitives';
import { PGA_PROGRAMS } from './lib/pga-programs';
import type { ImportDatabase, PgaAgency } from './lib/import-types';
import {
  ImportProductsTable,
  type ImportTableFilters,
  type ImportTableServerQuery,
} from './ImportProductsTable';
import { EntriesTable } from './EntriesTable';
import { ManualProductWizard } from './AddProductWizard';
import { BulkCsvUploadDialog } from './BulkCsvUploadDialog';
import {
  TEMPLATE_COLUMNS,
  csvEscape,
  DATABASE_NOTIFICATION_CHANNELS,
} from './CreateImportDatabaseDialog';
import {
  IMPORT_PRODUCT_SCREENING_LISTS,
  fetchImportDatabaseById,
  fetchImportDatabaseIssueSummary,
  fetchImportProductsByDatabaseId,
  fetchImportProductsSummaryByDatabaseId,
  toImportProduct,
  runImportDatabaseScreening,
  fetchImportDatabaseScreeningStatus,
  deleteImportDatabase,
  updateImportDatabaseSettings,
  fetchImportDatabaseNotifications,
  markImportDatabaseNotificationRead,
  markAllImportDatabaseNotificationsRead,
  dismissImportDatabaseNotification,
  createImportCargoWiseExport,
  createImportPdfReport,
  fetchImportProductExportJob,
  fetchImportCargoWiseExportReadiness,
  fetchImportDatabaseTariffExposure,
  fetchImportDatabasePgaExposure,
  downloadImportProductExport,
  downloadImportPdfReport,
  derivePgaFlags,
} from './lib/import-service';
import type {
  ImportServiceDatabase,
  ImportDatabaseIssueSummary,
  ImportServiceProduct,
  ImportScreeningStatus,
  ImportDatabaseNotification,
  ImportNotificationCounts,
  ImportNotificationView,
  ImportProductListQuery,
  ImportProductListSummary,
  ImportCargoWiseExportReadiness,
  ImportDatabaseTariffExposure,
  ImportDatabasePgaExposure,
  ImportProductExportJob,
  ImportProductExportWarning,
} from './lib/import-service';
import { toast } from './lib/toast';
import { isHiddenAdditionalColumn } from './lib/additional-columns';
import {
  isLegacySectionProgramBucket,
  resolveCsvDutyBreakdown,
} from './lib/tariff-export';
import { useImportHubReadOnly } from './lib/access';
import ImportTools from './tools/ImportTools';
import { defaultRenderHrefLink, defaultRenderProductLink } from './routing';
import type { RenderHrefLink, RenderProductLink } from './routing';
import { SendToBrokerWizard } from '../../components/BiteDB/Modal/SendToBroker';
import DatabaseEmailAudienceEditor from '../../components/BiteDB/Components/DatabaseEmailAudienceEditor';
import DatabaseRulesSettings from './sop/DatabaseRulesSettings';
import {
  DEFAULT_IMPORT_DATABASE_ICON,
  getImportDatabaseAccent,
  getImportDatabaseIcon,
  resolveImportDatabaseAccent,
} from './databaseAppearance';

type IconComp = ComponentType<IconProps>;

type Tab = 'dashboard' | 'products' | 'entries' | 'documents' | 'settings';
const TABS: Tab[] = [
  'dashboard',
  'products',
  'entries',
  'documents',
  'settings',
];
type PriorityView = 'issues' | 'notifications';

export interface ImportDatabaseFilters {
  q?: string;
  risk?: string[];
  pga?: string[];
  tariff?: string[];
  value?: string[];
  fta?: string[];
  origin?: string[];
  addDuty?: string[];
  score?: string[];
  classification?: string[];
  issues?: string[];
  hierarchy?: string[];
  ruleset?: string[];
}

export interface ImportDatabasePageProps {
  dbId: string;
  initialTab?: Tab;
  initialFilters?: ImportDatabaseFilters;
  onTabChange?: (tab: Tab) => void;
  onFiltersChange?: (filters: ImportDatabaseFilters) => void;
  onProductCreated?: (productId: string) => void;
  renderHubLink?: RenderHrefLink;
  renderProductLink?: RenderProductLink;
  /** Href used by the default hub link. Defaults to "/import". */
  hubHref?: string;
  /** Bumped by the host when products changed elsewhere; triggers a refetch. */
  dataVersion?: number;
  /** True while kept mounted but hidden behind a product page. */
  backgrounded?: boolean;
}

interface ExportWarningGroup {
  key: string;
  title: string;
  context?: string;
  message: string;
  tariffCodes: string[];
}

const getExportWarningTitle = (code: string): string => {
  switch (code) {
    case 'STALE_TARIFF_PRESERVED':
      return 'Tariff code preserved';
    case 'TARIFF_CODE_REMOVAL':
      return 'Obsolete tariff code reconciled';
    case 'UNSCREENED_PRODUCT':
      return 'Tariff enrichment unavailable';
    case 'UNSUPPORTED_PGA_MAPPING':
      return 'PGA data retained in Import Hub';
    case 'PRODUCT_WITHOUT_SOURCE':
      return 'Product not included';
    case 'UNMATCHED_SOURCE_RECORD':
      return 'Source record preserved';
    case 'GENERATED_PART_NUMBER':
      return 'Part number generated';
    default:
      return 'Export review required';
  }
};

const getExportWarningContext = (
  warning: ImportProductExportWarning,
): string | undefined => {
  const product = [warning.partNumber, warning.productName]
    .filter(Boolean)
    .join(' · ');
  return product || warning.externalId || undefined;
};

const groupExportWarnings = (
  warnings: ImportProductExportWarning[],
): ExportWarningGroup[] => {
  const groups: ExportWarningGroup[] = [];
  const tariffGroups = new Map<string, ExportWarningGroup>();

  warnings.forEach((warning, index) => {
    const context = getExportWarningContext(warning);
    if (
      warning.code === 'STALE_TARIFF_PRESERVED' ||
      warning.code === 'TARIFF_CODE_REMOVAL'
    ) {
      const productKey =
        warning.productId ?? warning.externalId ?? context ?? `row-${index}`;
      const key = `${warning.code}-${productKey}`;
      let group = tariffGroups.get(key);
      if (!group) {
        group = {
          key,
          title: getExportWarningTitle(warning.code),
          context,
          message:
            warning.code === 'TARIFF_CODE_REMOVAL'
              ? 'These codes were not returned by the latest Import Hub screening. The export clears or reuses their source tariff rows through import-safe merges.'
              : 'These codes were not returned by the latest Import Hub screening. They were preserved in the export so the original customer data can be reviewed safely.',
          tariffCodes: [],
        };
        tariffGroups.set(key, group);
        groups.push(group);
      }
      if (
        warning.tariffCode &&
        !group.tariffCodes.includes(warning.tariffCode)
      ) {
        group.tariffCodes.push(warning.tariffCode);
      }
      return;
    }

    groups.push({
      key: `${warning.code}-${
        warning.productId ?? warning.externalId ?? index
      }`,
      title: getExportWarningTitle(warning.code),
      context,
      message: warning.message,
      tariffCodes: warning.tariffCode ? [warning.tariffCode] : [],
    });
  });

  return groups;
};

const IMPORT_CSV_ENRICHMENT_HEADERS = [
  'base_duty_rate',
  'base_duty_cost',
  'additional_duty_rate',
  'additional_duty_cost',
  'total_duty_cost',
  'total_landed_cost',
  'section_301_applicable',
  'section_301_codes',
  'section_301_rate',
  'section_301_value',
  'section_301_duty_cost',
  'section_232_applicable',
  'section_232_codes',
  'section_232_rate',
  'section_232_value',
  'section_232_duty_cost',
  'other_additional_duty_cost',
  'has_pga_codes',
  'has_cpsc_hit',
  'pga_flag_codes',
  'ad_cvd',
  'ad_cvd_case_numbers',
  'last_screened_at',
] as const;

type ImportCsvEnrichmentHeader = (typeof IMPORT_CSV_ENRICHMENT_HEADERS)[number];

const IMPORT_CSV_ENRICHMENT_LABELS: Record<ImportCsvEnrichmentHeader, string> =
  {
    base_duty_rate: 'Base Duty Rate',
    base_duty_cost: 'Base Duty Cost',
    additional_duty_rate: 'Additional Duty Rate',
    additional_duty_cost: 'Additional Duty Cost',
    total_duty_cost: 'Total Duty Cost',
    total_landed_cost: 'Total Landed Cost',
    section_301_applicable: 'Section 301 Applicable',
    section_301_codes: 'Section 301 Codes',
    section_301_rate: 'Section 301 Rate',
    section_301_value: 'Section 301 Value',
    section_301_duty_cost: 'Section 301 Duty Cost',
    section_232_applicable: 'Section 232 Applicable',
    section_232_codes: 'Section 232 Codes',
    section_232_rate: 'Section 232 Rate',
    section_232_value: 'Section 232 Value',
    section_232_duty_cost: 'Section 232 Duty Cost',
    other_additional_duty_cost: 'Other Additional Duty Cost',
    has_pga_codes: 'Has PGA Codes',
    has_cpsc_hit: 'Has CPSC Hit',
    pga_flag_codes: 'PGA Flag Codes',
    ad_cvd: 'AD/CVD',
    ad_cvd_case_numbers: 'AD/CVD Case Numbers',
    last_screened_at: 'Last Screened At',
  };

const csvMetricNumber = (value: unknown): number | null => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Number(value.replace(/[^0-9.-]/g, ''))
      : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

// Matches the product service's cap on a scoped tariff-exposure request.
const SCOPED_EXPOSURE_MAX_IDS = 50000;

const csvMoney = (value: unknown): string => {
  const parsed = csvMetricNumber(value);
  return parsed == null ? '' : parsed.toFixed(2);
};

const csvPercent = (value: unknown): string => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^(free|none)$/i.test(trimmed) || trimmed.includes('%')) return trimmed;
  }
  const parsed = csvMetricNumber(value);
  return parsed == null ? '' : `${parsed.toFixed(2)}%`;
};

const csvBoolean = (value: unknown): string =>
  typeof value === 'boolean' ? String(value) : '';

const csvYesNo = (value: boolean): string => (value ? 'Yes' : 'No');

// Lists stored at screening; null means screened before they were stored, so
// the cell stays blank rather than claiming "No".
const csvStoredList = (value: unknown): string[] | null =>
  Array.isArray(value) ? value.map(String) : null;

// One Yes/No column per PGA agency (other than CPSC, which Has CPSC Hit
// covers) that at least one exported product is flagged for, in the badge
// list's order.
const pgaAgencyExportColumns = (products: ImportServiceProduct[]) => {
  const present = new Set(
    products.flatMap((product) => csvStoredList(product.pga_programs) ?? []),
  );
  return PGA_PROGRAMS.filter(
    (program) => program.code !== 'CPS' && present.has(program.code),
  ).map((program) => ({
    code: program.code,
    label: /PGA$/.test(program.short) ? program.short : `${program.short} PGA`,
  }));
};

// Duty figures exist only for a calculated result; an unavailable one (e.g. an
// expired HTS code) exports blank cells rather than 0.00.
const hasTariffCalculation = (product: ImportServiceProduct): boolean =>
  product.has_tariff_calculation ?? product.has_tariff_result === true;

const buildImportCsvEnrichment = (
  product: ImportServiceProduct,
  allocation?: {
    programs?: Record<string, unknown>;
    otherAdditional?: unknown;
  },
): Record<ImportCsvEnrichmentHeader, string> => {
  const hasTariffResult = hasTariffCalculation(product);
  const totalDuty = csvMetricNumber(product.duty_cost) ?? 0;
  const additionalDuty = csvMetricNumber(product.additional_duty_cost) ?? 0;
  const rawSection301 = csvMetricNumber(product.raw_section_301_duty_cost) ?? 0;
  const rawSection232 = csvMetricNumber(product.raw_section_232_duty_cost) ?? 0;
  const { section301Duty, section232Duty, otherAdditionalDuty } =
    resolveCsvDutyBreakdown({
      additionalDuty,
      rawSection301Duty: rawSection301,
      rawSection232Duty: rawSection232,
      allocation,
    });
  const hasScreeningResult = Boolean(
    product.last_screened_at || product.product_screening_created_at,
  );
  // pga_agencies alone misses CPSC hits that are only derivable from
  // all_results, which is where derivePgaFlags (and therefore every pgaFlags
  // read in the UI, e.g. the detail page's CPSC check) picks them up. Keep the
  // raw scan as well so an unnormalized agency value still counts.
  const hasCpscHit =
    derivePgaFlags(product).includes('CPSC') ||
    (Array.isArray(product.pga_agencies)
      ? product.pga_agencies.some((agency) => {
          const normalized = String(agency).trim().toUpperCase();
          return (
            normalized === 'CPSC' ||
            normalized === 'CONSUMER PRODUCT SAFETY COMMISSION'
          );
        })
      : false);

  const adcvdCases = csvStoredList(product.adcvd_cases);

  return {
    base_duty_rate: hasTariffResult ? csvPercent(product.base_duty_rate) : '',
    base_duty_cost: hasTariffResult
      ? csvMoney(Math.max(0, totalDuty - additionalDuty))
      : '',
    additional_duty_rate: hasTariffResult
      ? csvPercent(product.additional_duty_rate)
      : '',
    additional_duty_cost: hasTariffResult ? csvMoney(additionalDuty) : '',
    total_duty_cost: hasTariffResult ? csvMoney(totalDuty) : '',
    total_landed_cost: hasTariffResult ? csvMoney(product.total_cost) : '',
    section_301_applicable: hasTariffResult
      ? csvBoolean(product.section_301)
      : '',
    section_301_codes: product.section_301_codes ?? '',
    section_301_rate: hasTariffResult
      ? csvPercent(product.section_301_rate)
      : '',
    section_301_value: hasTariffResult
      ? csvMoney(product.section_301_value)
      : '',
    section_301_duty_cost: hasTariffResult ? csvMoney(section301Duty) : '',
    section_232_applicable: hasTariffResult
      ? csvBoolean(product.section_232)
      : '',
    section_232_codes: product.section_232_codes ?? '',
    section_232_rate: hasTariffResult
      ? csvPercent(product.section_232_rate)
      : '',
    section_232_value: hasTariffResult
      ? csvMoney(product.section_232_value)
      : '',
    section_232_duty_cost: hasTariffResult ? csvMoney(section232Duty) : '',
    other_additional_duty_cost: hasTariffResult
      ? csvMoney(otherAdditionalDuty)
      : '',
    has_pga_codes: hasScreeningResult ? csvBoolean(product.has_pga_codes) : '',
    has_cpsc_hit: hasScreeningResult ? csvBoolean(hasCpscHit) : '',
    pga_flag_codes: csvStoredList(product.pga_flag_codes)?.join(', ') ?? '',
    ad_cvd: adcvdCases ? csvYesNo(adcvdCases.length > 0) : '',
    ad_cvd_case_numbers: adcvdCases?.join(', ') ?? '',
    last_screened_at: product.last_screened_at ?? '',
  };
};

export const ImportDatabasePage: React.FC<ImportDatabasePageProps> = ({
  dbId,
  initialTab = 'dashboard',
  initialFilters,
  onTabChange,
  onFiltersChange,
  onProductCreated,
  renderHubLink = defaultRenderHrefLink,
  renderProductLink = defaultRenderProductLink,
  hubHref = '/import',
  dataVersion = 0,
  backgrounded = false,
}) => {
  const history = useHistory();
  const readOnly = useImportHubReadOnly();
  const [tab, setTabState] = useState<Tab>(initialTab);
  const [priorityView, setPriorityView] =
    useState<PriorityView>('notifications');
  const mockDb = IMPORT_DATABASES.find((d) => d.id === dbId);

  // Real databases (created via the product-service-backed home page) aren't in
  // the sample array. Fetch them by id and use neutral presentation defaults
  // for optional cosmetic fields so another database's demo metadata can never
  // leak into a live record.
  const [realDb, setRealDb] = useState<ImportServiceDatabase | null>(null);
  // Starts true (unless a mock hit means no fetch will ever run) so the first
  // render shows the loading state instead of a "not found" flash before the
  // fetch effect below has had a chance to run.
  const [realDbLoading, setRealDbLoading] = useState(!mockDb);
  const [sendToBrokerOpen, setSendToBrokerOpen] = useState(false);

  useEffect(() => {
    setTabState(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (mockDb) return;
    let cancelled = false;
    setRealDb(null);
    setRealDbLoading(true);
    fetchImportDatabaseById(dbId).then(({ success, database, error }) => {
      if (cancelled) return;
      setRealDbLoading(false);
      if (success && database) {
        setRealDb(database);
      } else {
        toast.error(error ?? 'Failed to load database');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dbId, mockDb]);

  const db: ImportDatabase | undefined =
    mockDb ??
    (realDb
      ? {
          id: realDb.id,
          name: realDb.name,
          description: realDb.description ?? '',
          ownerId: realDb.created_by ?? '',
          createdAt: realDb.created_at ?? '',
          icon: realDb.icon ?? DEFAULT_IMPORT_DATABASE_ICON,
          accent: getImportDatabaseAccent(realDb.accent_color),
          visibility: 'shared',
          databaseStructure:
            realDb.database_structure === 'flat' ||
            realDb.database_structure === 'hts-code-grouping'
              ? realDb.database_structure
              : 'hierarchy',
        }
      : undefined);

  const productsFilterId = dbId;

  const mockFallbackProducts = useMemo(
    () =>
      mockDb
        ? IMPORT_PRODUCTS.filter((p) => p.importDatabaseId === productsFilterId)
        : [],
    [mockDb, productsFilterId],
  );

  // Real products for a real database. `null` means the fetch has not returned
  // a definitive answer yet; real dashboards stay empty in that state rather
  // than showing unrelated mock product values.
  const [realProducts, setRealProducts] = useState<
    ImportServiceProduct[] | null
  >(null);
  const [realProductsDatabaseId, setRealProductsDatabaseId] = useState<
    string | null
  >(null);
  // Bumped when a whole-database screening run completes, to force the
  // effect below to refetch (products' risk/results can change after a run).
  const [realProductsVersion, setRealProductsVersion] = useState(0);
  const dataVersionRef = useRef(dataVersion);
  useEffect(() => {
    if (dataVersionRef.current === dataVersion) return;
    dataVersionRef.current = dataVersion;
    setRealProductsVersion((n) => n + 1);
  }, [dataVersion]);
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(!mockDb);
  const [productFacets, setProductFacets] = useState<{
    origins: string[];
    databases: Array<{ id: string; name: string }>;
  }>({ origins: [], databases: [] });
  const productFacetsDatabaseRef = useRef<string | null>(null);
  const [productQuery, setProductQuery] = useState<ImportProductListQuery>({
    limit: 25,
    offset: 0,
    sortBy: 'compliance_score',
    sortOrder: 'desc',
  });
  // The products table owns the filters and sort and reports them right after
  // it mounts (including filters handed in through initialFilters). The list
  // request waits for that report: fetching with the placeholder query above
  // first meant every Products-tab visit sent the request twice (the table's
  // query never matched the placeholder's shape), and the server ran count +
  // rows + facets for both.
  const [productQueryReady, setProductQueryReady] = useState(false);
  const [productSummary, setProductSummary] =
    useState<ImportProductListSummary | null>(null);
  const [productSummaryLoading, setProductSummaryLoading] = useState(!mockDb);
  // Dashboard exposure cards. Held here rather than in Dashboard (which
  // unmounts on tab switch) so they load once per summary, not per visit.
  const [tariffExposureData, setTariffExposureData] =
    useState<ImportDatabaseTariffExposure | null>(null);
  const [tariffExposureLoading, setTariffExposureLoading] = useState(!mockDb);
  const [pgaExposureData, setPgaExposureData] =
    useState<ImportDatabasePgaExposure | null>(null);
  const [pgaExposureLoading, setPgaExposureLoading] = useState(!mockDb);
  const [tariffExposureError, setTariffExposureError] = useState(false);
  const [pgaExposureError, setPgaExposureError] = useState(false);
  const [exposureRetry, setExposureRetry] = useState(0);
  // Tariff/PGA exposure load once per version of the database's data -- the
  // same events that reload the summary (screening finished, product edited)
  // -- and are requested alongside the summary rather than after it.
  const exposureLoadKey = `${dbId}:${realProductsVersion}:${exposureRetry}`;
  const exposureLoadKeyRef = useRef<string | null>(null);
  const productPageSize = 25;
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationRefreshVersion, setNotificationRefreshVersion] =
    useState(0);

  useEffect(() => {
    // Dashboard metrics come from the compact summary request. Do not make its
    // critical path compete with product rows, counts, and facets that are only
    // visible on the Products tab.
    if (mockDb || tab !== 'products') return;
    if (!productQueryReady) {
      setProductsLoading(true);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setProductsLoading(true);
    fetchImportProductsByDatabaseId(
      dbId,
      {
        ...productQuery,
        limit: productPageSize,
        offset: (productPage - 1) * productPageSize,
        includeFacets: productFacetsDatabaseRef.current !== dbId,
      },
      controller.signal,
    ).then(
      ({
        success,
        products: fetched,
        total,
        facets,
        facetsIncluded,
        error,
      }) => {
        if (cancelled) return;
        setProductsLoading(false);
        if (success) {
          const lastPage = Math.max(1, Math.ceil(total / productPageSize));
          if (productPage > lastPage) {
            setProductPage(lastPage);
            return;
          }
          setRealProducts(fetched);
          setRealProductsDatabaseId(dbId);
          setProductTotal(total);
          if (facetsIncluded) {
            setProductFacets(facets);
            productFacetsDatabaseRef.current = dbId;
          }
        } else {
          toast.error(error ?? 'Failed to load products');
        }
      },
    );
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    dbId,
    mockDb,
    productPage,
    productQuery,
    productQueryReady,
    realProductsVersion,
    tab,
  ]);

  // The table remounts on every visit to the Products tab, so wait for its
  // report again each time.
  useEffect(() => {
    if (tab !== 'products') setProductQueryReady(false);
  }, [tab]);

  useEffect(() => {
    setProductPage(1);
    setProductTotal(0);
    setProductSummary(null);
    setProductSummaryLoading(!mockDb);
    exposureLoadKeyRef.current = null;
    setTariffExposureData(null);
    setTariffExposureLoading(!mockDb);
    setPgaExposureData(null);
    setPgaExposureLoading(!mockDb);
    setTariffExposureError(false);
    setPgaExposureError(false);
    setRealProducts(null);
    setRealProductsDatabaseId(null);
    setProductFacets({ origins: [], databases: [] });
    productFacetsDatabaseRef.current = null;
    setProductsLoading(!mockDb);
  }, [dbId, mockDb]);

  useEffect(() => {
    if (mockDb) {
      setProductSummaryLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setProductSummaryLoading(true);
    fetchImportProductsSummaryByDatabaseId(dbId, controller.signal).then(
      ({ success, summary, error }) => {
        if (cancelled) return;
        setProductSummaryLoading(false);
        if (success) setProductSummary(summary);
        else toast.error(error ?? 'Failed to load product summary');
      },
    );
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [dbId, mockDb, realProductsVersion]);

  // Fetched on the first Dashboard visit after each data change and reused on
  // later visits. Neither request uses the summary's response, so they no
  // longer wait for it -- the Dashboard used to pay summary + exposure time
  // back to back.
  useEffect(() => {
    if (mockDb) {
      setTariffExposureLoading(false);
      setPgaExposureLoading(false);
      return;
    }
    if (tab !== 'dashboard') return;
    if (exposureLoadKeyRef.current === exposureLoadKey) return;
    const requestKey = exposureLoadKey;
    exposureLoadKeyRef.current = requestKey;
    // Ignore responses for a load (or database) that has since changed.
    const isCurrent = () => exposureLoadKeyRef.current === requestKey;
    // Drop the previous summary's figures so a failed refresh can't leave
    // them on screen as current.
    setTariffExposureData(null);
    setTariffExposureError(false);
    setTariffExposureLoading(true);
    fetchImportDatabaseTariffExposure(dbId)
      .catch(() => ({
        success: false,
        exposure: undefined as ImportDatabaseTariffExposure | undefined,
        error: undefined as string | undefined,
      }))
      .then(({ success, exposure, error }) => {
        if (!isCurrent()) return;
        setTariffExposureLoading(false);
        if (success && exposure) {
          setTariffExposureData(exposure);
        } else {
          setTariffExposureError(true);
          toast.error(error ?? 'Failed to load tariff exposure');
        }
      });
    setPgaExposureData(null);
    setPgaExposureError(false);
    setPgaExposureLoading(true);
    fetchImportDatabasePgaExposure(dbId)
      .catch(() => ({
        success: false,
        exposure: undefined as ImportDatabasePgaExposure | undefined,
        error: undefined as string | undefined,
      }))
      .then(({ success, exposure, error }) => {
        if (!isCurrent()) return;
        setPgaExposureLoading(false);
        if (success && exposure) {
          setPgaExposureData(exposure);
        } else {
          setPgaExposureError(true);
          toast.error(error ?? 'Failed to load PGA exposure');
        }
      });
  }, [dbId, mockDb, tab, exposureLoadKey]);

  const retryExposure = useCallback(() => {
    setExposureRetry((count) => count + 1);
  }, []);

  // Exposure data from an earlier load shows as loading until it refreshes.
  const exposureStale =
    !mockDb && exposureLoadKeyRef.current !== exposureLoadKey;

  const handleProductServerQuery = useCallback(
    (query: ImportTableServerQuery) => {
      const sortByMap: Record<ImportTableServerQuery['sortKey'], string> = {
        name: 'name',
        hts: 'hts_code',
        origin: 'country_of_origin',
        value: 'total_cost',
        baseDuty: 'base_duty_rate',
        addDuty: 'additional_duty_rate',
        flags: 'program_flags',
        score: 'compliance_score',
      };
      const next: ImportProductListQuery = {
        search: query.filters.q,
        risk: query.filters.risk,
        pga: query.filters.pga,
        tariff: query.filters.tariff,
        valueBuckets: query.filters.value,
        fta: query.filters.fta,
        origins: query.filters.origin,
        databaseIds: query.filters.database,
        additionalDutyBuckets: query.filters.addDuty,
        scoreBuckets: query.filters.score,
        classification: query.filters.classification,
        issueTypes: query.filters.issues,
        rulesetChanged: query.filters.ruleset?.includes('changed'),
        hierarchyTypes: query.filters.hierarchy?.length
          ? query.filters.hierarchy
          : undefined,
        sortBy: sortByMap[query.sortKey],
        sortOrder: query.sortDir,
      };
      setProductQuery((current) => {
        if (JSON.stringify(current) === JSON.stringify(next)) return current;
        setProductPage(1);
        return next;
      });
      setProductQueryReady(true);
    },
    [],
  );

  // Whole-database "Run Screening" (Slice 10) — real databases only. Progress
  // is tracked via the databaseId-keyed status endpoint (getScreeningProgress
  // needs a jobId this run never returns, and reads a Redis namespace the
  // product pipeline doesn't write — see the plan notes).
  //
  // Status is scoped by this exact database id and reconnects on mount so a
  // run started by another tab, teammate, or schedule still locks stale rows.
  const [screeningStatus, setScreeningStatus] =
    useState<ImportScreeningStatus | null>(null);
  const screeningActive =
    !!screeningStatus &&
    (screeningStatus.inProgress ||
      screeningStatus.aiInProgress ||
      screeningStatus.verificationInProgress);

  const unmountedRef = useRef(false);
  useEffect(() => {
    // React 18 Strict Mode and Fast Refresh can run an effect cleanup before
    // replaying its setup while preserving this ref. Reset it here so a live
    // page never mistakes that development lifecycle replay for an unmount and
    // skips the screening-status polling loop.
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // This page is reused across :dbId changes (react-router v5 doesn't
  // remount on a param-only change — same reason ImportProductDetailPage
  // needs its own stale-data guards). The epoch invalidates any recursive
  // poll continuation still in flight for a PREVIOUS database once dbId (or
  // mockDb) changes, since a setTimeout chain can't otherwise be cancelled.
  const screeningEpochRef = useRef(0);
  const wasScreeningActiveRef = useRef(false);
  const screeningPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // The ScreeningResult id that was current when Run Screening was pressed. Until
  // a poll reports either inProgress or a *different* id, the run we just started
  // has not appeared yet — without this guard the previous run's terminal row
  // satisfies the completion check and the overlay closes immediately. Same guard
  // pollImportScreeningStatus applies for single-product runs.
  const priorScreeningResultIdRef = useRef<string | null>(null);
  // Latest id any poll has seen. Read (not component state) when capturing the
  // baseline above, so a click that lands before the first render-committed status
  // still gets an accurate baseline.
  const lastPolledScreeningResultIdRef = useRef<string | null>(null);
  // Set once the start request has been accepted. From that point the endpoint has
  // definitely created the ScreeningResult row, so whatever the status endpoint
  // reports as latest is ours — which is how a run that finishes before the first
  // poll is still recognised even with no known baseline.
  const startRequestAcceptedRef = useRef(false);
  // True between pressing Run Screening and the first poll that observes the new
  // run. A ref rather than state because the recursive poll chain closes over the
  // render that scheduled it, so it cannot read current state.
  const startingRef = useRef(false);
  // Set once polling has confirmed our run exists. A start request that fails
  // *after* this must not roll the banner back: a large database can outlast the
  // proxy timeout while the run itself proceeds normally.
  const sawLiveRunRef = useRef(false);
  const screeningStartedAtRef = useRef<number | null>(null);
  // Fast polling only while a run is young. A 19k-product run lasts far longer
  // than the phase transitions worth catching at 1.5s, and an hour at that rate
  // is thousands of requests.
  const screeningFastPollIntervalMs = 1500;
  const screeningFastPollWindowMs = 30_000;
  const screeningActivePollIntervalMs = 5000;
  const screeningIdlePollIntervalMs = 10000;

  const nextScreeningPollDelay = () => {
    const startedAt = screeningStartedAtRef.current;
    if (startedAt == null) return screeningActivePollIntervalMs;
    return Date.now() - startedAt < screeningFastPollWindowMs
      ? screeningFastPollIntervalMs
      : screeningActivePollIntervalMs;
  };

  const scheduleScreeningStatusPoll = (
    databaseId: string,
    epoch: number,
    delay: number,
  ) => {
    if (screeningPollTimerRef.current) {
      clearTimeout(screeningPollTimerRef.current);
    }
    screeningPollTimerRef.current = setTimeout(() => {
      screeningPollTimerRef.current = null;
      if (unmountedRef.current || epoch !== screeningEpochRef.current) return;
      pollScreeningStatusOnce(databaseId, epoch);
    }, delay);
  };

  const pollScreeningStatusOnce = (databaseId: string, epoch: number) => {
    if (screeningPollTimerRef.current) {
      clearTimeout(screeningPollTimerRef.current);
      screeningPollTimerRef.current = null;
    }
    fetchImportDatabaseScreeningStatus(databaseId).then(
      ({ success, status }) => {
        if (unmountedRef.current || epoch !== screeningEpochRef.current) return;

        if (!success || !status) {
          // Transient failure — keep the loop alive with a fixed retry rather
          // than silently going idle, same fallback as pollImportScreeningStatus.
          scheduleScreeningStatusPoll(
            databaseId,
            epoch,
            nextScreeningPollDelay(),
          );
          return;
        }

        lastPolledScreeningResultIdRef.current = status.screeningResultId;

        const active =
          status.inProgress ||
          status.aiInProgress ||
          status.verificationInProgress;

        // A run we just started may not be visible yet: the server registers the
        // ScreeningResult row before publishing, but a poll can still land in that
        // gap (and `waitForReady` can delay registration further). Recognise our
        // run by it being live, by an id different from the baseline at click time,
        // or by the start request having been accepted — after which the latest row
        // is necessarily ours.
        const isOurRun =
          active ||
          (status.screeningResultId != null &&
            (status.screeningResultId !== priorScreeningResultIdRef.current ||
              startRequestAcceptedRef.current));

        // Hold the optimistic state until our run appears, so the previous run's
        // terminal row is never read as this one finishing. Backs off with the
        // normal cadence rather than staying at the fast interval indefinitely.
        if (startingRef.current && !isOurRun) {
          scheduleScreeningStatusPoll(
            databaseId,
            epoch,
            nextScreeningPollDelay(),
          );
          return;
        }

        if (isOurRun) {
          startingRef.current = false;
          // The run exists server-side; a later failure of the start request is
          // then a transport problem, not a run that never began.
          sawLiveRunRef.current = true;
        }

        setScreeningStatus(status);
        if (active) {
          wasScreeningActiveRef.current = true;
          if (screeningStartedAtRef.current == null) {
            screeningStartedAtRef.current = Date.now();
          }
          scheduleScreeningStatusPoll(
            databaseId,
            epoch,
            nextScreeningPollDelay(),
          );
          return;
        }

        screeningStartedAtRef.current = null;

        if (wasScreeningActiveRef.current) {
          wasScreeningActiveRef.current = false;
          setRealProductsVersion((n) => n + 1);
          // ScreeningResult becomes terminal just before the coordinator
          // dispatches its in-app notification. Refresh a few times across
          // that short handoff so the unread badge updates without requiring
          // the user to open Priority Issues → Notifications.
          [250, 1500, 4000, 8000].forEach((delay) => {
            setTimeout(() => {
              if (
                !unmountedRef.current &&
                epoch === screeningEpochRef.current
              ) {
                setNotificationRefreshVersion((n) => n + 1);
              }
            }, delay);
          });
          if (status.status === 'failed') {
            toast.error(
              'Screening completed with errors. Existing results were preserved and the database remains eligible for retry.',
            );
          } else {
            toast.success('Screening complete.');
          }
        }

        // Keep a lightweight status watch alive while idle so screenings
        // started by another tab, teammate, or scheduler are detected and the
        // table locks before externally updated rows become stale.
        scheduleScreeningStatusPoll(
          databaseId,
          epoch,
          screeningIdlePollIntervalMs,
        );
      },
    );
  };

  // On dbId change, invalidate the previous database's polling chain and
  // inspect this exact database's current run. This reconnects another tab,
  // browser, or scheduled screening without allowing a different database's
  // status to lock this page.
  useEffect(() => {
    screeningEpochRef.current += 1;
    wasScreeningActiveRef.current = false;
    startingRef.current = false;
    sawLiveRunRef.current = false;
    startRequestAcceptedRef.current = false;
    screeningStartedAtRef.current = null;
    priorScreeningResultIdRef.current = null;
    lastPolledScreeningResultIdRef.current = null;
    if (screeningPollTimerRef.current) {
      clearTimeout(screeningPollTimerRef.current);
      screeningPollTimerRef.current = null;
    }
    setScreeningStatus(null);
    if (!mockDb) {
      pollScreeningStatusOnce(dbId, screeningEpochRef.current);
    }
    return () => {
      if (screeningPollTimerRef.current) {
        clearTimeout(screeningPollTimerRef.current);
        screeningPollTimerRef.current = null;
      }
    };
  }, [dbId, mockDb]);

  const handleRunScreening = () => startDatabaseScreening(false);

  const [aiEvaluationConfirmOpen, setAiEvaluationConfirmOpen] = useState(false);
  const handleRunAiEvaluation = () => {
    setAiEvaluationConfirmOpen(false);
    startDatabaseScreening(true);
  };

  const startDatabaseScreening = (runAiEvaluation: boolean) => {
    // Optimistically show the banner + lock the table immediately, so a fast
    // (small-database) run is visibly reflected instead of only appearing on
    // the first poll several seconds later.
    priorScreeningResultIdRef.current =
      lastPolledScreeningResultIdRef.current ??
      screeningStatus?.screeningResultId ??
      null;
    startingRef.current = true;
    sawLiveRunRef.current = false;
    startRequestAcceptedRef.current = false;
    screeningStartedAtRef.current = Date.now();
    setScreeningStatus({
      screeningResultId: null,
      databaseProductIds: [],
      inProgress: true,
      screeningInProgress: true,
      finalizing: false,
      aiInProgress: false,
      aiRequested: runAiEvaluation,
      verificationInProgress: false,
      verificationPhase: null,
      status: 'in-progress',
      percent: 0,
      chunkProcessed: null,
      chunkTotal: null,
      postChunkProcessed: null,
      publishedChunks: null,
      totalProducts: null,
      productsProcessed: null,
      postProductsProcessed: null,
      postPercent: null,
      startedAt: new Date().toISOString(),
      starting: true,
    });
    wasScreeningActiveRef.current = true;

    // Start polling now rather than in the response handler. The server seeds the
    // run's progress record before it produces any work, so the status endpoint
    // reports it almost immediately — whereas the start request itself returns
    // only once the whole run has been queued.
    pollScreeningStatusOnce(dbId, screeningEpochRef.current);

    runImportDatabaseScreening(dbId, { runAiEvaluation }).then(
      ({ success, error }) => {
        if (unmountedRef.current) return;
        if (!success) {
          // Only roll back if polling never saw the run. A slow start request can
          // time out at the proxy while the run is progressing perfectly well, and
          // tearing down the banner there would strand the user with an error over
          // a healthy screening.
          if (sawLiveRunRef.current) {
            console.warn(
              '[import-hub] Screening start request failed after the run was already live; keeping progress UI',
              error,
            );
            return;
          }
          startingRef.current = false;
          wasScreeningActiveRef.current = false;
          screeningStartedAtRef.current = null;
          setScreeningStatus(null);
          toast.error(error ?? 'Failed to start screening');
          return;
        }
        startRequestAcceptedRef.current = true;
        toast.info(
          runAiEvaluation
            ? 'AI evaluation started. Large databases can take a while.'
            : 'Screening started. Large databases can take a while.',
        );
        pollScreeningStatusOnce(dbId, screeningEpochRef.current);
      },
    );
  };

  const products = useMemo(() => {
    if (mockDb) return mockFallbackProducts;
    if (realProducts && realProductsDatabaseId === dbId)
      return realProducts.map((row) => toImportProduct(row, dbId));
    return [];
  }, [
    mockDb,
    realProducts,
    realProductsDatabaseId,
    mockFallbackProducts,
    dbId,
  ]);
  const entries = useMemo(
    () => getEntriesForDatabase(productsFilterId),
    [productsFilterId],
  );
  useEffect(() => {
    setUnreadNotificationCount(0);
  }, [dbId, mockDb]);

  useEffect(() => {
    if (mockDb) return;
    // DbNotifications updates this badge from its paginated response while
    // mounted, so polling the counts endpoint at the same time is redundant.
    if ((tab === 'dashboard' && priorityView === 'notifications') || backgrounded)
      return;

    let cancelled = false;
    let inFlight = false;
    let activeController: AbortController | null = null;
    const refreshCounts = () => {
      if (inFlight) return;
      inFlight = true;
      activeController = new AbortController();
      fetchImportDatabaseNotifications(
        dbId,
        'all',
        false,
        1,
        0,
        true,
        activeController.signal,
      )
        .then(({ success, counts: nextCounts }) => {
          if (!cancelled && success) {
            setUnreadNotificationCount(nextCounts.unread);
          }
        })
        .finally(() => {
          inFlight = false;
        });
    };
    refreshCounts();
    const interval = window.setInterval(refreshCounts, 15_000);
    return () => {
      cancelled = true;
      activeController?.abort();
      window.clearInterval(interval);
    };
  }, [backgrounded, dbId, mockDb, notificationRefreshVersion, tab, priorityView]);

  // CRUD actions (Slice B) — real databases only, mirrors the legacy Products
  // Database tool's Add Item / Import Data / Export Data / Delete Database.
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // "Import Data" now opens a small chooser first (file vs. documents)
  // instead of jumping straight into the CSV dialog -- a single starting
  // point matching the Create Database wizard's start-mode choice. Choosing
  // "Upload documents" navigates to the Customs Entry Analyzer page rather
  // than opening a third dialog.
  const [uploadChooserOpen, setUploadChooserOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingDb, setDeletingDb] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportProductIds, setExportProductIds] = useState<string[] | null>(
    null,
  );
  const [exportQuery, setExportQuery] = useState<ImportProductListQuery>({});
  const [exportFormat, setExportFormat] = useState<
    'csv' | 'xlsx' | 'xml' | 'pdf'
  >('csv');
  const [preparingExport, setPreparingExport] = useState(false);
  const [exportFileName, setExportFileName] = useState('');
  const [exportJob, setExportJob] = useState<ImportProductExportJob | null>(
    null,
  );
  const [exportError, setExportError] = useState<string | null>(null);
  const [xmlReadiness, setXmlReadiness] =
    useState<ImportCargoWiseExportReadiness | null>(null);
  const [xmlReadinessLoading, setXmlReadinessLoading] = useState(false);
  const xmlNeedsScreening =
    exportFormat === 'xml' && (xmlReadiness?.unscreened ?? 0) > 0;
  const pdfExportInProgress =
    exportJob?.format === 'pdf-report' && preparingExport;
  const exportWarningGroups = useMemo(
    () => groupExportWarnings(exportJob?.warnings ?? []),
    [exportJob?.warnings],
  );
  // Re-checked whenever screening finishes so the button unlocks once results land.
  useEffect(() => {
    if (!exportOpen || exportFormat !== 'xml' || mockDb || screeningActive) {
      setXmlReadinessLoading(false);
      return;
    }
    let cancelled = false;
    setXmlReadiness(null);
    setXmlReadinessLoading(true);
    void (async () => {
      try {
        const result = await fetchImportCargoWiseExportReadiness({
          databaseId: dbId,
          productIds: exportProductIds ?? undefined,
        });
        // A failed check doesn't block export; the server re-validates on start.
        if (!cancelled && result.success) {
          setXmlReadiness(result.readiness ?? null);
        }
      } catch {
        // Same fallback as a failed check: leave export enabled.
      } finally {
        if (!cancelled) setXmlReadinessLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dbId,
    exportFormat,
    exportOpen,
    exportProductIds,
    mockDb,
    screeningActive,
  ]);

  const closeExportDialog = useCallback(() => {
    setExportOpen(false);
    setExportJob(null);
    setExportError(null);
    setPreparingExport(false);
  }, []);

  useEffect(() => {
    closeExportDialog();
    setExportProductIds(null);
  }, [closeExportDialog, dbId]);

  useEffect(() => {
    if (!pdfExportInProgress) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [pdfExportInProgress]);

  const loadProductsForExport = async (
    query: ImportProductListQuery,
    productIds?: string[] | null,
  ): Promise<ImportServiceProduct[] | null> => {
    const rows: ImportServiceProduct[] = [];
    const batchSize = 100;
    // A selection that fits in one request is fetched by id, so the server
    // sorts it exactly as the table does. Larger selections page through the
    // filtered products (the caller keeps the selected ones), since sorting
    // separate id batches would break the table's order.
    const scopedQuery =
      productIds?.length && productIds.length <= batchSize
        ? { ...query, productIds }
        : query;
    let offset = 0;
    let expectedTotal = Number.POSITIVE_INFINITY;
    while (offset < expectedTotal) {
      const result = await fetchImportProductsByDatabaseId(dbId, {
        ...scopedQuery,
        limit: batchSize,
        offset,
      });
      if (!result.success) {
        toast.error(result.error ?? 'Failed to prepare product export');
        return null;
      }
      rows.push(...result.products);
      expectedTotal = result.total;
      if (result.products.length === 0) break;
      offset += result.products.length;
    }
    return rows;
  };

  const openExportData = (
    query: ImportProductListQuery = {},
    productIds?: string[],
  ) => {
    if ((productSummary?.total ?? productTotal) === 0) {
      toast.warning('No products to export.');
      return;
    }
    setExportQuery(query);
    setExportProductIds(productIds?.length ? productIds : null);
    setExportFormat('csv');
    setExportJob(null);
    setExportError(null);
    setExportFileName(
      `${db?.name ?? 'products'}_products_${new Date()
        .toISOString()
        .slice(0, 10)}`,
    );
    setExportOpen(true);
  };

  const downloadProductExport = (
    productsToExport: ImportServiceProduct[],
    tariffExposure: ImportDatabaseTariffExposure | undefined,
    format: 'csv' | 'xlsx',
  ) => {
    const selectedIdSet = exportProductIds ? new Set(exportProductIds) : null;
    const scopedProducts = selectedIdSet
      ? productsToExport.filter((product) => selectedIdSet.has(product.id))
      : productsToExport;
    if (scopedProducts.length === 0) {
      toast.warning('No products match the selected export scope.');
      return;
    }
    const additionalKeys = new Set<string>();
    scopedProducts.forEach((p) => {
      const extra = p.additional_columns;
      if (extra && typeof extra === 'object') {
        Object.keys(extra).forEach((k) => {
          if (!isHiddenAdditionalColumn(k)) additionalKeys.add(k);
        });
      }
    });
    const templateColumnSet = new Set<string>(TEMPLATE_COLUMNS);
    const occupiedHeaders = new Set<string>();
    const reserveHeader = (label: string, duplicatePrefix: string): string => {
      let header = label;
      let duplicateIndex = 1;
      while (occupiedHeaders.has(header.toLowerCase())) {
        header = `${duplicatePrefix} ${label}${
          duplicateIndex === 1 ? '' : ` ${duplicateIndex}`
        }`;
        duplicateIndex += 1;
      }
      occupiedHeaders.add(header.toLowerCase());
      return header;
    };
    /**
     * Everything that came from the user keeps its canonical identifier as the
     * header, because normHeader only lowercases and turns spaces into
     * underscores before the importer looks for an exact match. Template
     * columns would otherwise miss autoMap entirely (display labels like
     * "Transportation Method" or "Country of Smelt/Pour" do not map back to
     * trans_method / country_of_smelt_pour), and additional columns would be
     * re-imported under a renamed key whenever the original was camelCase or
     * hyphenated (fooBar and foo-bar both collapse to foo_bar). Only the BITE
     * enrichment columns below carry display labels, and those labels are
     * chosen so normHeader maps them straight back to their source name.
     */
    const existingExportColumns = [
      ...TEMPLATE_COLUMNS.map((source) => ({
        source,
        header: reserveHeader(source, 'BITE'),
        kind: 'template' as const,
      })),
      ...Array.from(additionalKeys)
        .filter((source) => !templateColumnSet.has(source))
        .map((source) => ({
          source,
          header: reserveHeader(source, 'Additional'),
          kind: 'additional' as const,
        })),
    ];
    const enrichmentColumns = IMPORT_CSV_ENRICHMENT_HEADERS.map((source) => {
      const label = IMPORT_CSV_ENRICHMENT_LABELS[source];
      return { source, header: reserveHeader(label, 'BITE') };
    });
    // The agency columns sit right after Has CPSC Hit.
    const agencyInsertIndex =
      enrichmentColumns.findIndex(({ source }) => source === 'has_cpsc_hit') +
      1;
    const agencyColumns = pgaAgencyExportColumns(scopedProducts).map(
      ({ code, label }) => ({ code, header: reserveHeader(label, 'BITE') }),
    );
    // One duty column per tariff program the exported products are exposed
    // to, in the same order as the dashboard's Tariff exposure card.
    const tariffAllocationFor = (product: ImportServiceProduct) =>
      product.database_product_id
        ? tariffExposure?.products?.[product.database_product_id]
        : undefined;
    const programDutiesFor = (product: ImportServiceProduct) =>
      tariffAllocationFor(product)?.programs;
    const programColumns = (tariffExposure?.programs ?? [])
      .filter(
        ({ bucket }) =>
          !isLegacySectionProgramBucket(bucket) &&
          scopedProducts.some(
            (product) => programDutiesFor(product)?.[bucket] !== undefined,
          ),
      )
      .map(({ bucket, label }) => ({
        bucket,
        header: reserveHeader(`${label} Duty Cost`, 'BITE'),
      }));
    const withAgencyColumns = <T,>(enrichment: T[], agencies: T[]): T[] => [
      ...enrichment.slice(0, agencyInsertIndex),
      ...agencies,
      ...enrichment.slice(agencyInsertIndex),
    ];
    const exportHeaders = [
      ...existingExportColumns.map(({ header }) => header),
      ...withAgencyColumns(
        enrichmentColumns.map(({ header }) => header),
        agencyColumns.map(({ header }) => header),
      ),
      ...programColumns.map(({ header }) => header),
    ];
    const exportRows: string[][] = [];
    scopedProducts.forEach((p) => {
      const tariffAllocation = tariffAllocationFor(p);
      const enrichment = buildImportCsvEnrichment(p, tariffAllocation);
      const row = existingExportColumns.map(({ source, kind }) => {
        if (kind === 'template') {
          const value =
            source === 'parent'
              ? p.parent_model_number
              : (p as unknown as Record<string, unknown>)[source];
          return value == null ? '' : String(value);
        }
        const extra = p.additional_columns;
        const value =
          extra && typeof extra === 'object' ? extra[source] : undefined;
        return value == null ? '' : String(value);
      });
      const programs = csvStoredList(p.pga_programs);
      row.push(
        ...withAgencyColumns(
          enrichmentColumns.map(({ source }) => enrichment[source]),
          agencyColumns.map(({ code }) =>
            programs ? csvYesNo(programs.includes(code)) : '',
          ),
        ),
      );
      const programDuties = tariffAllocation?.programs;
      row.push(
        ...programColumns.map(({ bucket }) =>
          hasTariffCalculation(p) ? csvMoney(programDuties?.[bucket] ?? 0) : '',
        ),
      );
      exportRows.push(row);
    });
    const requestedName = exportFileName.trim() || 'products_export';
    const fileName = requestedName.toLowerCase().endsWith(`.${format}`)
      ? requestedName
      : `${requestedName}.${format}`;
    if (format === 'xlsx') {
      // Cells stay text, as in the CSV, so codes and SKUs keep leading zeros.
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet([exportHeaders, ...exportRows]),
        'Products',
      );
      XLSX.writeFile(workbook, fileName);
    } else {
      const lines = [
        exportHeaders.map(csvEscape).join(','),
        ...exportRows.map((row) => row.map((v) => csvEscape(v)).join(',')),
      ];
      const blob = new Blob([lines.join('\n')], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
    closeExportDialog();
    toast.success(
      `Exported ${scopedProducts.length} product${
        scopedProducts.length === 1 ? '' : 's'
      }.`,
    );
  };

  const handleExportData = async () => {
    if (preparingExport || exportJob) return;
    let keepPdfExportBusy = false;
    setExportError(null);
    setPreparingExport(true);
    try {
      if (exportFormat === 'csv' || exportFormat === 'xlsx') {
        const matchingProducts = await loadProductsForExport(
          exportQuery,
          exportProductIds,
        );
        if (!matchingProducts) return;
        // Only request tariff exposure for the exported rows, unless the
        // export is the whole database or too large to list (the product
        // service accepts up to SCOPED_EXPOSURE_MAX_IDS ids).
        const selectedIdSet = exportProductIds
          ? new Set(exportProductIds)
          : null;
        const exportedIds = matchingProducts
          .filter((product) => !selectedIdSet || selectedIdSet.has(product.id))
          .map((product) => product.database_product_id)
          .filter((id): id is string => Boolean(id));
        const wholeDatabase =
          (!exportProductIds &&
            matchingProducts.length >= (productSummary?.total ?? Infinity)) ||
          exportedIds.length > SCOPED_EXPOSURE_MAX_IDS;
        const exposureResult =
          mockDb || exportedIds.length === 0
            ? null
            : await fetchImportDatabaseTariffExposure(dbId, {
                includeProducts: true,
                databaseProductIds: wholeDatabase ? undefined : exportedIds,
              });
        if (exposureResult && !exposureResult.success) {
          toast.warning(
            'Tariff program columns could not be added to this export.',
          );
        }
        downloadProductExport(
          matchingProducts,
          exposureResult?.exposure,
          exportFormat,
        );
        return;
      }

      if (exportFormat === 'pdf') {
        const {
          limit: _limit,
          offset: _offset,
          includeFacets: _includeFacets,
          databaseIds: _databaseIds,
          ...reportFilters
        } = exportQuery;
        const result = await createImportPdfReport({
          databaseId: dbId,
          fileName: exportFileName,
          productIds: exportProductIds ?? undefined,
          filters: exportProductIds ? undefined : reportFilters,
        });
        if (!result.success || !result.jobId) {
          const message = result.error ?? 'Failed to start PDF report export';
          setExportError(message);
          toast.error(message);
          return;
        }
        setExportJob({
          jobId: result.jobId,
          databaseId: dbId,
          status: 'queued',
          fileName: exportFileName,
          format: 'pdf-report',
          mode: 'report',
          processedProducts: 0,
          totalProducts: exportProductIds?.length ?? 0,
          warnings: [],
          warningCount: 0,
        });
        keepPdfExportBusy = true;
        return;
      }

      const result = await createImportCargoWiseExport({
        databaseId: dbId,
        fileName: exportFileName,
        productIds: exportProductIds ?? undefined,
      });
      if (!result.success || !result.jobId) {
        const message = result.error ?? 'Failed to start CargoWise XML export';
        setExportError(message);
        toast.error(message);
        return;
      }
      setExportJob({
        jobId: result.jobId,
        databaseId: dbId,
        status: 'queued',
        fileName: exportFileName,
        format: 'cargowise-xml',
        mode: result.mode ?? 'source-aware',
        processedProducts: 0,
        totalProducts: exportProductIds?.length ?? 0,
        warnings: [],
        warningCount: 0,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to prepare export';
      setExportError(message);
      toast.error(message);
    } finally {
      if (!keepPdfExportBusy) setPreparingExport(false);
    }
  };

  const downloadPdfJob = useCallback(
    async (job: ImportProductExportJob) => {
      setPreparingExport(true);
      setExportError(null);
      try {
        const result = await downloadImportPdfReport(
          job.jobId,
          job.resultFileName ?? exportFileName,
        );
        if (!result.success) {
          const message = result.error ?? 'Failed to download PDF report';
          setExportError(message);
          toast.error(message);
          return;
        }
        toast.success('PDF report exported.');
        closeExportDialog();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to download PDF report';
        setExportError(message);
        toast.error(message);
      } finally {
        setPreparingExport(false);
      }
    },
    [closeExportDialog, exportFileName],
  );

  useEffect(() => {
    if (
      !exportOpen ||
      !exportJob ||
      ['completed', 'failed', 'cancelled'].includes(exportJob.status)
    ) {
      return;
    }
    let cancelled = false;
    let timer: number | undefined = undefined;
    let failedAttempts = 0;
    const poll = async () => {
      const result = await fetchImportProductExportJob(exportJob.jobId);
      if (cancelled) return;
      if (!result.success || !result.job) {
        failedAttempts += 1;
        const retryLimit = 5;
        if (failedAttempts >= retryLimit) {
          setExportError(
            `${
              result.error ?? 'Unable to load export progress'
            }. Automatic polling has stopped; close the dialog and try again.`,
          );
          if (exportJob.format === 'pdf-report') setPreparingExport(false);
          return;
        }
        setExportError(
          result.error ?? 'Failed to load export progress; retrying…',
        );
        timer = window.setTimeout(
          poll,
          Math.min(15_000, 2500 * 2 ** (failedAttempts - 1)),
        );
        return;
      }
      failedAttempts = 0;
      setExportError(null);
      setExportJob(result.job);
      if (result.job.status === 'failed' || result.job.status === 'cancelled') {
        setExportError(result.job.error ?? 'Export failed');
        if (result.job.format === 'pdf-report') setPreparingExport(false);
        return;
      }
      if (
        result.job.status === 'completed' &&
        result.job.format === 'pdf-report'
      ) {
        await downloadPdfJob(result.job);
        return;
      }
      if (result.job.status !== 'completed') {
        timer = window.setTimeout(poll, 1500);
      }
    };
    timer = window.setTimeout(poll, 700);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [downloadPdfJob, exportJob?.jobId, exportJob?.status, exportOpen]);

  const handleDownloadXml = async () => {
    if (!exportJob?.uploadId) return;
    setPreparingExport(true);
    const result = await downloadImportProductExport(
      exportJob.uploadId,
      exportJob.resultFileName ?? `${exportFileName}.xml`,
    );
    setPreparingExport(false);
    if (!result.success) {
      toast.error(result.error ?? 'Failed to download CargoWise XML export');
      return;
    }
    toast.success('CargoWise XML export downloaded.');
  };

  const handleDownloadPdf = async () => {
    if (!exportJob?.jobId) return;
    await downloadPdfJob(exportJob);
  };

  const handleDeleteDatabase = () => {
    setDeletingDb(true);
    deleteImportDatabase(dbId).then(({ success, error }) => {
      setDeletingDb(false);
      if (!success) {
        toast.error(error ?? 'Failed to delete database');
        return;
      }
      toast.success('Database deleted');
      setDeleteOpen(false);
      history.push(hubHref);
    });
  };

  if (!db) {
    if (realDbLoading) {
      return (
        <div className='ih-page'>
          <div className='ih-empty-page'>
            <span
              className='ih-row ih-gap-2'
              style={{ justifyContent: 'center', alignItems: 'center' }}
            >
              <span
                className='ih-spinner'
                style={{ width: 16, height: 16, borderWidth: 2 }}
              />
              Loading database…
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className='ih-page'>
        <div className='ih-empty-page'>
          Import database not found.{' '}
          {renderHubLink({
            href: hubHref,
            className: 'ih-btn-link',
            children: 'Back to Import Hub',
          })}
        </div>
      </div>
    );
  }

  const Ico = getImportDatabaseIcon(db.icon);
  const databaseAccent = resolveImportDatabaseAccent(
    getImportDatabaseAccent(db.accent),
  );
  const setTab = (next: Tab) => {
    setTabState(next);
    onTabChange?.(next);
  };
  const showIssueType = (issueType: string) => {
    const params = new URLSearchParams(history.location.search);
    params.set('tab', 'products');
    params.delete('issue');
    params.append('issue', issueType);
    history.replace(`${history.location.pathname}?${params.toString()}`);
    setTabState('products');
  };

  return (
    <div className='ih-page ih-database-shell'>
      <Prompt
        when={pdfExportInProgress}
        message='A PDF export is still in progress. Wait for the download to finish before leaving this page.'
      />
      <nav
        className='ih-breadcrumb ih-database-breadcrumb'
        aria-label='Breadcrumb'
      >
        {renderHubLink({
          href: hubHref,
          className: 'ih-crumb',
          children: (
            <>
              <Home size={14} /> Import Hub
            </>
          ),
        })}
        <ChevronRight size={12} />
        <span className='ih-crumb-current'>{db.name}</span>
      </nav>

      <header className='ih-database-header'>
        <div className='ih-database-identity'>
          <div
            className='ih-db-hero-icon'
            style={{
              background: `color-mix(in oklab, ${databaseAccent} 18%, transparent)`,
              color: databaseAccent,
            }}
          >
            <Ico size={24} />
          </div>
          <div className='ih-grow'>
            <div className='ih-row-wrap ih-gap-2'>
              <h1 className='ih-page-title'>{db.name}</h1>
            </div>
            <p className='ih-database-description'>{db.description || '—'}</p>
            <p className='ih-database-meta'>
              Created {db.createdAt ? formatDate(db.createdAt) : '—'}
            </p>
          </div>
        </div>
        {!mockDb && (
          <div className='ih-database-header-actions'>
            <CreditMeterButton packType='database' size='sm' />
            <Button
              variant='primary'
              className='ih-database-primary-action'
              locked={readOnly}
              onClick={() => setSendToBrokerOpen(true)}
              disabled={sendToBrokerOpen}
            >
              <Send size={18} /> Share Database
            </Button>
            <Button
              variant='outline'
              size='icon'
              title='Add Item'
              locked={readOnly}
              onClick={() => setAddItemOpen(true)}
            >
              <Plus size={16} />
            </Button>
            <Button
              variant='outline'
              size='icon'
              title='Import Data'
              locked={readOnly}
              onClick={() => setUploadChooserOpen(true)}
            >
              <Upload size={16} />
            </Button>
            <Button
              variant='outline'
              size='icon'
              title='Export Data'
              onClick={() => void openExportData()}
              disabled={preparingExport}
            >
              <Download size={16} />
            </Button>
          </div>
        )}
      </header>

      <div className='ih-database-tab-row'>
        <nav className='ih-tabs ih-database-tabs' role='tablist'>
          <SubTab
            active={tab === 'dashboard'}
            onClick={() => setTab('dashboard')}
            icon={LayoutDashboard}
            label='Dashboard'
          />
          <SubTab
            active={tab === 'products'}
            onClick={() => setTab('products')}
            icon={Package}
            label={`Products (${
              mockDb ? products.length : productSummary?.total ?? productTotal
            })`}
          />
          {/* <SubTab active={tab === "entries"} onClick={() => setTab("entries")} icon={Truck} label={`Entry Packages (${entries.length})`} /> */}
          <SubTab
            active={tab === 'documents'}
            onClick={() => setTab('documents')}
            icon={FileStack}
            label='Documents'
          />
        </nav>
        <div className='ih-database-tab-actions'>
          <Button
            variant='outline'
            size='icon'
            className={cx(
              'ih-database-tab-action',
              tab === 'settings' && 'is-active',
            )}
            title='Settings'
            aria-label='Settings'
            aria-pressed={tab === 'settings'}
            onClick={() => setTab('settings')}
          >
            <Settings size={16} />
          </Button>
          {!mockDb && (
            <Button
              variant='danger'
              size='icon'
              className='ih-database-tab-action'
              title='Delete Database'
              aria-label='Delete Database'
              locked={readOnly}
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 size={16} />
            </Button>
          )}
        </div>
      </div>

      <div role='tabpanel'>
        {tab === 'dashboard' && (
          <Dashboard
            products={products}
            summary={productSummary}
            summaryLoading={productSummaryLoading}
            tariffExposureData={tariffExposureData}
            tariffExposureLoading={tariffExposureLoading || exposureStale}
            pgaExposureData={pgaExposureData}
            pgaExposureLoading={pgaExposureLoading || exposureStale}
            tariffExposureError={tariffExposureError}
            pgaExposureError={pgaExposureError}
            onRetryExposure={retryExposure}
            databaseId={dbId}
            isRealDatabase={!mockDb}
            renderProductLink={renderProductLink}
            onIssueFilter={showIssueType}
            priorityView={priorityView}
            onPriorityViewChange={setPriorityView}
            unreadNotificationCount={unreadNotificationCount}
            onUnreadNotificationCountChange={setUnreadNotificationCount}
            onOpenSettings={() => setTab('settings')}
          />
        )}
        {tab === 'products' && (
          <ProductsTab
            products={products}
            totalProducts={mockDb ? products.length : productTotal}
            page={productPage}
            pageSize={productPageSize}
            loading={productsLoading}
            facets={productFacets}
            dbId={dbId}
            databaseStructure={db.databaseStructure ?? 'hierarchy'}
            initialFilters={initialFilters}
            onFiltersChange={onFiltersChange}
            renderProductLink={renderProductLink}
            isRealDb={!mockDb}
            screeningStatus={screeningStatus}
            screeningActive={screeningActive}
            onRunScreening={handleRunScreening}
            onRunAiEvaluation={() => setAiEvaluationConfirmOpen(true)}
            onProductsChanged={() => setRealProductsVersion((n) => n + 1)}
            onPageChange={setProductPage}
            onServerQueryChange={handleProductServerQuery}
            onExportProducts={(productIds) =>
              void openExportData(productQuery, productIds)
            }
          />
        )}
        {tab === 'entries' && (
          <div className='ih-col ih-gap-3'>
            <div>
              <h2 className='ih-section-title' style={{ fontSize: 16 }}>
                Entry Packages
              </h2>
              <p className='ih-text-xs ih-text-muted ih-mt-1'>
                Import events, associated products, and supporting documents for
                this database.
              </p>
            </div>
            <EntriesTable
              entries={entries}
              renderProductLink={renderProductLink}
            />
          </div>
        )}
        {tab === 'documents' && (
          <ImportTools
            key={dbId}
            variant='database'
            databaseId={dbId}
            databaseName={db.name}
          />
        )}
        {tab === 'settings' &&
          (realDb ? (
            <DatabaseSettingsTab
              database={realDb}
              onUpdated={(updated) => setRealDb(updated)}
            />
          ) : (
            <div className='ih-card ih-db-settings-unavailable'>
              Settings can be edited after this sample database is created as a
              live Import Hub database.
            </div>
          ))}
      </div>

      <ManualProductWizard
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        databaseId={dbId}
        databaseName={db.name}
        databaseStructure={db.databaseStructure ?? 'hierarchy'}
        onCreated={() => setRealProductsVersion((n) => n + 1)}
        onProductCreated={(id) => onProductCreated?.(id)}
      />
      <BulkCsvUploadDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        databaseId={dbId}
        databaseStructure={db.databaseStructure ?? 'hierarchy'}
        onImported={() => setRealProductsVersion((n) => n + 1)}
        onBack={() => {
          setImportOpen(false);
          setUploadChooserOpen(true);
        }}
      />
      <Dialog open={uploadChooserOpen} onOpenChange={setUploadChooserOpen}>
        <DialogContent size='lg' onClose={() => setUploadChooserOpen(false)}>
          <DialogHeader>
            <div className='ih-dialog-icon-header'>
              <span className='ih-dialog-icon-badge'>
                <Upload size={20} />
              </span>
              <div className='ih-dialog-icon-copy'>
                <DialogTitle>Import Data</DialogTitle>
                <p className='ih-dialog-subtitle'>
                  Choose how you&apos;d like to add products to this database.
                </p>
              </div>
            </div>
          </DialogHeader>
          <DialogBody>
            <div className='ih-import-choice-grid'>
              <button
                type='button'
                className='ih-import-choice-card'
                onClick={() => {
                  setUploadChooserOpen(false);
                  setImportOpen(true);
                }}
              >
                <div className='ih-import-choice-icon'>
                  <Upload size={22} />
                </div>
                <div className='ih-import-choice-title'>Import from file</div>
                <p className='ih-import-choice-body'>
                  Drop a CSV, map columns, validate, and import many products at
                  once.
                </p>
              </button>
              <button
                type='button'
                className='ih-import-choice-card'
                onClick={() => {
                  setUploadChooserOpen(false);
                  history.push('/customs-entry-analyzer', {
                    presetDatabaseId: dbId,
                    presetDatabaseName: db.name,
                    returnTo: {
                      label: db.name,
                      path: `/import-hub/db/${dbId}`,
                    },
                  });
                }}
              >
                <div className='ih-import-choice-icon'>
                  <FileStack size={22} />
                </div>
                <div className='ih-import-choice-title'>Upload documents</div>
                <p className='ih-import-choice-body'>
                  Parse customs entry PDFs and send the results into this
                  database.
                </p>
              </button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      <SendToBrokerWizard
        database={{
          id: dbId,
          name: db.name,
          database_structure: db.databaseStructure,
        }}
        open={sendToBrokerOpen}
        handleClose={() => setSendToBrokerOpen(false)}
        readRecipientEmail
      />
      <Dialog
        open={exportOpen}
        onOpenChange={(open) => {
          if (open) setExportOpen(true);
          else if (!pdfExportInProgress) closeExportDialog();
        }}
      >
        <DialogContent
          size='md'
          onClose={pdfExportInProgress ? undefined : closeExportDialog}
        >
          <DialogHeader>
            <div className='ih-dialog-icon-header'>
              <span className='ih-dialog-icon-badge'>
                <Download size={20} />
              </span>
              <div className='ih-dialog-icon-copy'>
                <DialogTitle>Export data</DialogTitle>
                <p className='ih-dialog-subtitle'>
                  Enter a name and choose your export format.
                </p>
              </div>
            </div>
          </DialogHeader>
          <DialogBody>
            <div className='ih-col ih-gap-4'>
              <label className='ih-col ih-gap-2'>
                <span className='ih-label'>File name</span>
                <Input
                  value={exportFileName}
                  disabled={Boolean(exportJob)}
                  onChange={(event) => setExportFileName(event.target.value)}
                  placeholder='e.g. Q4 Tariff Check'
                />
              </label>
              <div className='ih-col ih-gap-2'>
                <span className='ih-label'>Download format</span>
                <button
                  type='button'
                  className={cx(
                    'ih-radio-option ih-export-format-option',
                    exportFormat === 'csv' && 'is-selected',
                  )}
                  disabled={Boolean(exportJob)}
                  onClick={() => setExportFormat('csv')}
                >
                  <span
                    className={cx(
                      'ih-radio-dot',
                      exportFormat === 'csv' && 'is-selected',
                    )}
                  >
                    <span className='ih-radio-dot-inner' />
                  </span>
                  <span>
                    <strong>BITE Default CSV</strong>
                    <small>
                      Spreadsheet-ready product data from the current view.
                    </small>
                  </span>
                </button>
                <button
                  type='button'
                  className={cx(
                    'ih-radio-option ih-export-format-option',
                    exportFormat === 'xlsx' && 'is-selected',
                  )}
                  disabled={Boolean(exportJob)}
                  onClick={() => setExportFormat('xlsx')}
                >
                  <span
                    className={cx(
                      'ih-radio-dot',
                      exportFormat === 'xlsx' && 'is-selected',
                    )}
                  >
                    <span className='ih-radio-dot-inner' />
                  </span>
                  <span>
                    <strong>BITE Default Excel</strong>
                    <small>
                      The same columns as the CSV, as an .xlsx workbook.
                    </small>
                  </span>
                </button>
                <button
                  type='button'
                  className={cx(
                    'ih-radio-option ih-export-format-option',
                    exportFormat === 'xml' && 'is-selected',
                  )}
                  disabled={Boolean(exportJob)}
                  onClick={() => setExportFormat('xml')}
                >
                  <span
                    className={cx(
                      'ih-radio-dot',
                      exportFormat === 'xml' && 'is-selected',
                    )}
                  >
                    <span className='ih-radio-dot-inner' />
                  </span>
                  <span>
                    <strong>CargoWise XML</strong>
                    <small>
                      Enriches an uploaded CargoWise document or generates a new
                      file from mapped product and tariff data.
                    </small>
                  </span>
                </button>
                <button
                  type='button'
                  className={cx(
                    'ih-radio-option ih-export-format-option',
                    exportFormat === 'pdf' && 'is-selected',
                  )}
                  disabled={Boolean(exportJob)}
                  onClick={() => setExportFormat('pdf')}
                >
                  <span
                    className={cx(
                      'ih-radio-dot',
                      exportFormat === 'pdf' && 'is-selected',
                    )}
                  >
                    <span className='ih-radio-dot-inner' />
                  </span>
                  <span>
                    <strong>PDF Report</strong>
                    <small>
                      Branded database report with compliance, risk, tariff
                      exposure, and a product appendix.
                    </small>
                  </span>
                </button>
              </div>
              <p className='ih-text-xs ih-text-muted'>
                {exportProductIds
                  ? `${exportProductIds.length} product${
                      exportProductIds.length === 1 ? '' : 's'
                    } from the current view will be exported.`
                  : exportFormat === 'xml'
                  ? 'Import Hub will preserve and enrich an original CargoWise XML when available, or generate a new mapped XML for CSV and spreadsheet databases.'
                  : exportFormat === 'pdf'
                  ? 'The report will reflect products matching the current filtered view at the time it is generated.'
                  : 'Products matching the current filtered view will be exported.'}
              </p>
              {exportFormat === 'pdf' && !exportJob && (
                <div className='ih-export-note'>
                  <Info size={15} />
                  <span>
                    The PDF follows the standard BITE report design and includes
                    summary metrics, priority issues, trade exposure, and
                    product-level results.
                  </span>
                </div>
              )}
              {xmlNeedsScreening && !exportJob && xmlReadiness && (
                <div className='ih-export-note ih-export-note-warning'>
                  <AlertTriangle size={15} />
                  <div className='ih-col ih-gap-2'>
                    <span>
                      {xmlReadiness.unscreened === xmlReadiness.total
                        ? `This database hasn't been screened yet.`
                        : `${xmlReadiness.unscreened} of ${xmlReadiness.total} products haven't been screened yet.`}{' '}
                      XML export needs screening results, so run screening
                      before creating the XML.
                    </span>
                    <div>
                      <Button
                        variant='outline'
                        onClick={handleRunScreening}
                        disabled={screeningActive || readOnly}
                        title={
                          readOnly ? 'Requires a Business license' : undefined
                        }
                      >
                        {screeningActive ? (
                          <>
                            <span className='ih-spinner ih-export-spinner' />{' '}
                            Screening…
                          </>
                        ) : (
                          <>
                            <Sparkles size={14} /> Run Screening
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {exportFormat === 'xml' && !exportJob && !xmlNeedsScreening && (
                <div className='ih-export-note'>
                  <Info size={15} />
                  <span>
                    If this database has an original CargoWise XML, its source
                    records and unknown fields will be preserved. Otherwise, a
                    new XML will be generated from mapped Import Hub product and
                    tariff fields.
                  </span>
                </div>
              )}
              {exportJob && (
                <div className='ih-export-job' aria-live='polite'>
                  <div className='ih-export-job-heading'>
                    <span>
                      {exportJob.status === 'completed'
                        ? exportJob.format === 'pdf-report' && preparingExport
                          ? 'Downloading PDF…'
                          : 'Export ready'
                        : exportJob.status === 'failed' ||
                          exportJob.status === 'cancelled'
                        ? 'Export stopped'
                        : exportJob.status === 'uploading'
                        ? exportJob.format === 'pdf-report'
                          ? 'Uploading PDF report…'
                          : 'Uploading XML…'
                        : exportJob.status === 'generating'
                        ? exportJob.format === 'pdf-report'
                          ? 'Building PDF report…'
                          : exportJob.mode === 'generated'
                          ? 'Generating CargoWise records…'
                          : 'Enriching CargoWise records…'
                        : exportJob.status === 'preparing'
                        ? exportJob.format === 'pdf-report'
                          ? 'Collecting report data…'
                          : exportJob.mode === 'generated'
                          ? 'Preparing generated XML…'
                          : 'Preparing source XML…'
                        : 'Export queued…'}
                    </span>
                    {!['completed', 'failed', 'cancelled'].includes(
                      exportJob.status,
                    ) && <span className='ih-spinner ih-export-spinner' />}
                  </div>
                  {exportJob.totalProducts > 0 &&
                    exportJob.status !== 'completed' && (
                      <div className='ih-export-progress'>
                        <span
                          style={{
                            width: `${Math.min(
                              100,
                              Math.round(
                                (exportJob.processedProducts /
                                  exportJob.totalProducts) *
                                  100,
                              ),
                            )}%`,
                          }}
                        />
                      </div>
                    )}
                  {exportJob.format !== 'pdf-report' && exportJob.summary && (
                    <div className='ih-export-summary'>
                      {exportJob.mode === 'generated' ? (
                        <>
                          <span>
                            <strong>
                              {exportJob.summary.generatedProducts ??
                                exportJob.totalProducts}
                            </strong>{' '}
                            generated
                          </span>
                          <span>
                            <strong>
                              {exportJob.summary.enrichedProducts}
                            </strong>{' '}
                            tariff enriched
                          </span>
                        </>
                      ) : (
                        <>
                          <span>
                            <strong>
                              {exportJob.summary.enrichedProducts}
                            </strong>{' '}
                            enriched
                          </span>
                          <span>
                            <strong>
                              {exportJob.summary.unchangedProducts}
                            </strong>{' '}
                            unchanged
                          </span>
                        </>
                      )}
                      {exportJob.summary.unscreenedProducts > 0 && (
                        <span>
                          <strong>
                            {exportJob.summary.unscreenedProducts}
                          </strong>{' '}
                          without tariff enrichment
                        </span>
                      )}
                      {(exportJob.summary.tariffRowsCleared ??
                        exportJob.summary.tariffRowsMarkedForDeletion ??
                        0) > 0 && (
                        <span>
                          <strong>
                            {exportJob.summary.tariffRowsCleared ??
                              exportJob.summary.tariffRowsMarkedForDeletion}
                          </strong>{' '}
                          obsolete tariff row
                          {(exportJob.summary.tariffRowsCleared ??
                            exportJob.summary.tariffRowsMarkedForDeletion) === 1
                            ? ''
                            : 's'}{' '}
                          reconciled
                        </span>
                      )}
                      <span>
                        <strong>{exportJob.warningCount}</strong> notices
                      </span>
                    </div>
                  )}
                  {exportJob.format !== 'pdf-report' &&
                    exportJob.mode === 'generated' &&
                    exportJob.status === 'completed' && (
                      <div className='ih-export-note'>
                        <Info size={15} />
                        <span>
                          This XML was generated without an original CargoWise
                          source file. It contains mapped product and tariff
                          fields only. Review it before importing into
                          CargoWise.
                        </span>
                      </div>
                    )}
                  {exportJob.warnings.length > 0 && (
                    <details className='ih-export-warnings'>
                      <summary>
                        Review {exportJob.warningCount} export notice
                        {exportJob.warningCount === 1 ? '' : 's'}
                      </summary>
                      <ul>
                        {exportWarningGroups.map((warning) => (
                          <li key={warning.key}>
                            <div className='ih-export-warning-heading'>
                              <strong>{warning.title}</strong>
                              {warning.context && (
                                <span>{warning.context}</span>
                              )}
                            </div>
                            {warning.tariffCodes.length > 0 && (
                              <div className='ih-export-warning-codes'>
                                {warning.tariffCodes.map((code) => (
                                  <span key={code}>{code}</span>
                                ))}
                              </div>
                            )}
                            <p>{warning.message}</p>
                          </li>
                        ))}
                      </ul>
                      {exportJob.warningCount > exportJob.warnings.length && (
                        <p>
                          Showing the first {exportJob.warnings.length}{' '}
                          warnings. The full count is included in the export
                          summary.
                        </p>
                      )}
                    </details>
                  )}
                </div>
              )}
              {exportError && (
                <div className='ih-export-error'>{exportError}</div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={closeExportDialog}
              disabled={pdfExportInProgress}
            >
              {pdfExportInProgress
                ? 'Exporting…'
                : exportJob
                ? 'Close'
                : 'Cancel'}
            </Button>
            {exportJob?.status === 'completed' ? (
              <Button
                variant='primary'
                onClick={
                  exportJob.format === 'pdf-report'
                    ? handleDownloadPdf
                    : handleDownloadXml
                }
                disabled={
                  preparingExport ||
                  (exportJob.format !== 'pdf-report' && !exportJob.uploadId)
                }
              >
                {preparingExport && exportJob.format === 'pdf-report' ? (
                  <span className='ih-spinner ih-export-spinner' />
                ) : (
                  <Download size={14} />
                )}{' '}
                {exportJob.format === 'pdf-report'
                  ? preparingExport
                    ? 'Downloading PDF…'
                    : exportError
                    ? 'Retry PDF Download'
                    : 'Download PDF'
                  : 'Download XML'}
              </Button>
            ) : exportJob &&
              ['failed', 'cancelled'].includes(exportJob.status) ? (
              <Button
                variant='primary'
                onClick={() => {
                  setExportJob(null);
                  setExportError(null);
                }}
              >
                <RefreshCw size={14} /> Retry
              </Button>
            ) : (
              <Button
                variant='primary'
                onClick={handleExportData}
                disabled={
                  preparingExport ||
                  Boolean(exportJob) ||
                  !exportFileName.trim() ||
                  (exportFormat === 'xml' &&
                    (xmlNeedsScreening ||
                      xmlReadinessLoading ||
                      screeningActive))
                }
              >
                {preparingExport ? (
                  <span className='ih-spinner ih-export-spinner' />
                ) : (
                  <Download size={14} />
                )}
                {exportFormat === 'xml'
                  ? 'Create XML'
                  : exportFormat === 'pdf'
                  ? 'Export PDF'
                  : exportFormat === 'xlsx'
                  ? 'Download Excel'
                  : 'Download CSV'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={aiEvaluationConfirmOpen}
        onOpenChange={setAiEvaluationConfirmOpen}
      >
        <DialogContent
          size='md'
          onClose={() => setAiEvaluationConfirmOpen(false)}
        >
          <DialogHeader>
            <DialogTitle>Run AI evaluation?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className='ih-col ih-gap-2 ih-text-sm ih-text-muted'>
              <p>
                This screens all{' '}
                <strong>
                  {(productSummary?.total ?? productTotal).toLocaleString()}
                </strong>{' '}
                products in <strong>{db.name}</strong> and runs the AI
                evaluation for each, the same as Analyze Product.
              </p>
              <p>
                Large databases can take a while. Screening and other
                evaluations for this database can&apos;t start until it
                finishes.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant='ghost'
              onClick={() => setAiEvaluationConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              locked={readOnly}
              onClick={handleRunAiEvaluation}
            >
              Run AI evaluation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteOpen}
        onOpenChange={(v) => {
          if (!deletingDb) setDeleteOpen(v);
        }}
      >
        <DialogContent size='md' onClose={() => setDeleteOpen(false)}>
          <DialogHeader>
            <DialogTitle>Delete database?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className='ih-text-sm ih-text-muted'>
              This permanently deletes <strong>{db.name}</strong> and every
              product, screening result, and entity link in it. This cannot be
              undone.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              variant='ghost'
              onClick={() => setDeleteOpen(false)}
              disabled={deletingDb}
            >
              Cancel
            </Button>
            <Button
              variant='danger'
              locked={readOnly}
              onClick={handleDeleteDatabase}
              disabled={deletingDb}
            >
              {deletingDb ? 'Deleting…' : 'Delete database'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SETTINGS_FREQUENCIES = [
  'Once',
  'Daily',
  'Weekly',
  'Monthly',
  'Yearly',
] as const;
const SETTINGS_WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

function DatabaseSettingsTab({
  database,
  onUpdated,
}: {
  database: ImportServiceDatabase;
  onUpdated: (database: ImportServiceDatabase) => void;
}) {
  const readOnly = useImportHubReadOnly();
  const [frequency, setFrequency] = useState(
    database.screening_frequency || 'Once',
  );
  const [dayOfWeek, setDayOfWeek] = useState(
    database.screening_day_of_week || 'Monday',
  );
  const [selectedLists, setSelectedLists] = useState<string[]>(
    Array.isArray(database.screening_lists) &&
      database.screening_lists.length > 0
      ? database.screening_lists
      : [...IMPORT_PRODUCT_SCREENING_LISTS],
  );
  const [notificationChannels, setNotificationChannels] = useState<string[]>(
    (database.notifications_email ?? '')
      .split(',')
      .map((channel) => channel.trim())
      .filter(Boolean),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFrequency(database.screening_frequency || 'Once');
    setDayOfWeek(database.screening_day_of_week || 'Monday');
    setSelectedLists(
      Array.isArray(database.screening_lists) &&
        database.screening_lists.length > 0
        ? database.screening_lists
        : [...IMPORT_PRODUCT_SCREENING_LISTS],
    );
    setNotificationChannels(
      (database.notifications_email ?? '')
        .split(',')
        .map((channel) => channel.trim())
        .filter(Boolean),
    );
  }, [database]);

  const allListsSelected =
    selectedLists.length === IMPORT_PRODUCT_SCREENING_LISTS.length &&
    IMPORT_PRODUCT_SCREENING_LISTS.every((list) =>
      selectedLists.includes(list),
    );

  const toggleScreeningList = (list: string, checked: boolean) => {
    setSelectedLists((current) =>
      checked
        ? current.includes(list)
          ? current
          : [...current, list]
        : current.filter((item) => item !== list),
    );
  };

  const saveSettings = async () => {
    if (selectedLists.length === 0) {
      toast.warning('Select at least one screening list.');
      return;
    }

    setSaving(true);
    const {
      success,
      database: updated,
      error,
    } = await updateImportDatabaseSettings(database.id, {
      screeningFrequency: frequency,
      screeningDayOfWeek: frequency === 'Weekly' ? dayOfWeek : null,
      notificationChannels,
      screeningLists: selectedLists,
    });
    setSaving(false);

    if (!success || !updated) {
      toast.error(error ?? 'Failed to update database settings');
      return;
    }

    onUpdated(updated);
    toast.success('Settings saved successfully.');
  };

  return (
    <div className='ih-db-settings ih-db-settings-page'>
      <div className='ih-db-settings-heading'>
        <Settings size={20} />
        <div>
          <h2>Database Settings</h2>
          <p>
            Configure automated screening and notifications for {database.name}.
          </p>
        </div>
      </div>

      <section className='ih-db-settings-section'>
        <div className='ih-db-settings-section-title'>
          <List size={15} />
          Screening Lists
        </div>
        <p className='ih-db-settings-help'>
          Select the lists these products are screened against. Clearing a list
          omits it from future screening runs.
        </p>
        <label className='ih-db-settings-option ih-db-settings-option-all'>
          <input
            type='checkbox'
            checked={allListsSelected}
            disabled={readOnly}
            onChange={(event) =>
              setSelectedLists(
                event.target.checked ? [...IMPORT_PRODUCT_SCREENING_LISTS] : [],
              )
            }
          />
          <span>
            <strong>All screening lists</strong>
            <small>
              {selectedLists.length} of {IMPORT_PRODUCT_SCREENING_LISTS.length}{' '}
              selected
            </small>
          </span>
        </label>
        <div className='ih-db-settings-list-grid'>
          {IMPORT_PRODUCT_SCREENING_LISTS.map((list) => (
            <label key={list} className='ih-db-settings-option'>
              <input
                type='checkbox'
                checked={selectedLists.includes(list)}
                disabled={readOnly}
                onChange={(event) =>
                  toggleScreeningList(list, event.target.checked)
                }
              />
              <span>{list}</span>
            </label>
          ))}
        </div>
        <div className='ih-db-settings-notice'>
          <Info size={16} />
          <div>
            <strong>AI Screening Notice</strong>
            <p>
              AI-powered screening checks are automatically disabled for results
              containing more than 250 hits.
            </p>
          </div>
        </div>
      </section>

      <section className='ih-db-settings-section'>
        <div className='ih-db-settings-section-title'>
          <Clock size={15} />
          Screening Frequency
        </div>
        <div className='ih-db-settings-grid'>
          <label>
            <span>Frequency</span>
            <select
              className='ih-select'
              value={frequency}
              disabled={readOnly}
              onChange={(event) => setFrequency(event.target.value)}
            >
              {SETTINGS_FREQUENCIES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          {frequency === 'Weekly' && (
            <label>
              <span>Day of Week</span>
              <select
                className='ih-select'
                value={dayOfWeek}
                disabled={readOnly}
                onChange={(event) => setDayOfWeek(event.target.value)}
              >
                {SETTINGS_WEEKDAYS.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </section>

      <DatabaseRulesSettings
        databaseId={database.id}
        databaseName={database.name}
      />

      <section className='ih-db-settings-section'>
        <div className='ih-db-settings-section-title'>
          <Bell size={15} />
          Notifications
        </div>
        <p className='ih-db-settings-help'>
          Get notified when screening results are available.
        </p>
        <div className='ih-db-settings-options'>
          {DATABASE_NOTIFICATION_CHANNELS.map((channel) => (
            <label key={channel} className='ih-db-settings-option'>
              <input
                type='checkbox'
                checked={notificationChannels.includes(channel)}
                disabled={readOnly}
                onChange={(event) =>
                  setNotificationChannels((current) =>
                    event.target.checked
                      ? [...current, channel]
                      : current.filter((item) => item !== channel),
                  )
                }
              />
              <span>{channel}</span>
            </label>
          ))}
        </div>
        {/* Saved with its own button, as on entity databases' settings. */}
        {/* A disabled fieldset gives every nested control native disabled
            behavior, so read-only users can't reach Save from the keyboard. */}
        <fieldset
          className={cx(
            'ih-db-settings-email-audience',
            readOnly && 'is-locked',
          )}
          disabled={readOnly}
        >
          <DatabaseEmailAudienceEditor
            databaseId={database.id}
            variant='product'
          />
        </fieldset>
      </section>

      <div className='ih-db-settings-actions'>
        <Button
          variant='primary'
          locked={readOnly}
          onClick={saveSettings}
          disabled={saving || selectedLists.length === 0}
        >
          {saving ? (
            <span className='ih-spinner ih-db-settings-spinner' />
          ) : (
            <Save size={15} />
          )}
          {saving ? 'Saving…' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}

function DbNotifications({
  databaseId,
  isRealDatabase,
  onUnreadCountChange,
  onOpenSettings,
}: {
  databaseId: string;
  isRealDatabase: boolean;
  onUnreadCountChange: (count: number) => void;
  onOpenSettings: () => void;
}) {
  const readOnly = useImportHubReadOnly();
  const history = useHistory();
  const [notifications, setNotifications] = useState<
    ImportDatabaseNotification[]
  >([]);
  const [counts, setCounts] = useState<ImportNotificationCounts>({
    all: 0,
    unread: 0,
    system: 0,
    user: 0,
  });
  const [view, setView] = useState<ImportNotificationView>('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(isRealDatabase);
  const [error, setError] = useState<string | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const quietRefreshRef = useRef(false);
  const pageSize = 25;

  useEffect(() => setPage(1), [databaseId, unreadOnly, view]);

  useEffect(() => {
    if (!isRealDatabase) {
      setNotifications([]);
      setCounts({ all: 0, unread: 0, system: 0, user: 0 });
      setLoading(false);
      setError(null);
      onUnreadCountChange(0);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const quietRefresh = quietRefreshRef.current;
    quietRefreshRef.current = false;
    if (!quietRefresh) setLoading(true);
    setError(null);
    fetchImportDatabaseNotifications(
      databaseId,
      view,
      unreadOnly,
      pageSize,
      (page - 1) * pageSize,
      false,
      controller.signal,
    ).then(
      ({
        success,
        notifications: result,
        counts: nextCounts,
        total: nextTotal,
        error,
      }) => {
        if (cancelled) return;
        setLoading(false);
        if (!success) {
          if (!quietRefresh) {
            setNotifications([]);
            setError(error ?? 'Failed to load notifications');
          }
          return;
        }
        if (result.length === 0 && nextTotal > 0 && page > 1) {
          setPage((current) => Math.max(1, current - 1));
          return;
        }
        setNotifications(result);
        setCounts(nextCounts);
        setTotal(nextTotal);
        onUnreadCountChange(nextCounts.unread);
      },
    );

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    databaseId,
    isRealDatabase,
    onUnreadCountChange,
    page,
    refreshVersion,
    unreadOnly,
    view,
  ]);

  useEffect(() => {
    if (!isRealDatabase) return;
    const interval = window.setInterval(() => {
      quietRefreshRef.current = true;
      setRefreshVersion((current) => current + 1);
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [databaseId, isRealDatabase]);

  const TYPE_META: Record<string, { label: string; cls: string }> = {
    screening: { label: 'Screening', cls: 'ih-badge-info' },
    bitedb: { label: 'Compliance', cls: 'ih-badge-warning' },
    alert: { label: 'Alert', cls: 'ih-badge-danger' },
    'tariff-change': { label: 'Tariff change', cls: 'ih-badge-info' },
  };

  const markRead = async (notification: ImportDatabaseNotification) => {
    if (notification.read || busyId) return;
    const previousNotifications = notifications;
    const previousCounts = counts;
    const previousTotal = total;
    const nextUnread = Math.max(0, counts.unread - 1);

    setNotifications((current) =>
      unreadOnly
        ? current.filter((item) => item.id !== notification.id)
        : current.map((item) =>
            item.id === notification.id ? { ...item, read: true } : item,
          ),
    );
    if (unreadOnly) setTotal((current) => Math.max(0, current - 1));
    setCounts((current) => ({ ...current, unread: nextUnread }));
    onUnreadCountChange(nextUnread);
    setBusyId(notification.id);
    try {
      const result = await markImportDatabaseNotificationRead(
        databaseId,
        notification.id,
      );
      if (!result.success) {
        setNotifications(previousNotifications);
        setCounts(previousCounts);
        setTotal(previousTotal);
        onUnreadCountChange(previousCounts.unread);
        toast.error(result.error ?? 'Failed to mark notification as read');
        return;
      }
      quietRefreshRef.current = true;
      setRefreshVersion((current) => current + 1);
      // eslint-disable-next-line no-catch-shadow
    } catch (error) {
      setNotifications(previousNotifications);
      setCounts(previousCounts);
      setTotal(previousTotal);
      onUnreadCountChange(previousCounts.unread);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to mark notification as read',
      );
    } finally {
      setBusyId(null);
    }
  };

  const markAllRead = async () => {
    if (markingAll || counts.unread === 0) return;
    const previousNotifications = notifications;
    const previousCounts = counts;
    const previousTotal = total;

    setNotifications((current) =>
      unreadOnly ? [] : current.map((item) => ({ ...item, read: true })),
    );
    if (unreadOnly) setTotal(0);
    setCounts((current) => ({ ...current, unread: 0 }));
    onUnreadCountChange(0);
    setMarkingAll(true);
    try {
      const result = await markAllImportDatabaseNotificationsRead(databaseId);
      if (!result.success) {
        setNotifications(previousNotifications);
        setCounts(previousCounts);
        setTotal(previousTotal);
        onUnreadCountChange(previousCounts.unread);
        toast.error(result.error ?? 'Failed to mark all notifications as read');
        return;
      }
      quietRefreshRef.current = true;
      setRefreshVersion((current) => current + 1);
    } catch (err) {
      setNotifications(previousNotifications);
      setCounts(previousCounts);
      setTotal(previousTotal);
      onUnreadCountChange(previousCounts.unread);
      toast.error(
        err instanceof Error
          ? err.message
          : 'Failed to mark all notifications as read',
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const dismiss = async (
    event: React.MouseEvent,
    notification: ImportDatabaseNotification,
  ) => {
    event.stopPropagation();
    if (busyId) return;
    const previousNotifications = notifications;
    const previousCounts = counts;
    const previousTotal = total;
    const nextAll = Math.max(0, counts.all - 1);
    const nextUnread = Math.max(0, counts.unread - (notification.read ? 0 : 1));

    setNotifications((current) =>
      current.filter((item) => item.id !== notification.id),
    );
    setCounts((current) => ({
      all: nextAll,
      unread: nextUnread,
      system: Math.max(0, current.system - (notification.system ? 1 : 0)),
      user: Math.max(0, current.user - (notification.system ? 0 : 1)),
    }));
    setTotal((current) => Math.max(0, current - 1));
    onUnreadCountChange(nextUnread);
    setBusyId(notification.id);
    try {
      const result = await dismissImportDatabaseNotification(
        databaseId,
        notification.id,
      );
      if (!result.success) {
        setNotifications(previousNotifications);
        setCounts(previousCounts);
        setTotal(previousTotal);
        onUnreadCountChange(previousCounts.unread);
        toast.error(result.error ?? 'Failed to dismiss notification');
        return;
      }
      quietRefreshRef.current = true;
      setRefreshVersion((current) => current + 1);
      toast.success('Notification dismissed');
      // eslint-disable-next-line no-catch-shadow
    } catch (error) {
      setNotifications(previousNotifications);
      setCounts(previousCounts);
      setTotal(previousTotal);
      onUnreadCountChange(previousCounts.unread);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to dismiss notification',
      );
    } finally {
      setBusyId(null);
    }
  };
  const openNotification = async (notification: ImportDatabaseNotification) => {
    if (!readOnly) {
      await markRead(notification);
    }
    if (notification.link) {
      history.push(notification.link);
    }
  };

  if (!isRealDatabase) {
    return (
      <div className='ih-card ih-notification-state'>
        Notifications are available for live Import Hub databases.
      </div>
    );
  }

  return (
    <div className='ih-col ih-gap-3'>
      <div className='ih-notification-toolbar'>
        <div className='ih-notification-filters'>
          {(
            [
              ['all', `All (${counts.all})`],
              ['system', `System (${counts.system})`],
              ['user', `User (${counts.user})`],
            ] as Array<[ImportNotificationView, string]>
          ).map(([value, label]) => (
            <Button
              key={value}
              variant={view === value && !unreadOnly ? 'primary' : 'outline'}
              size='sm'
              onClick={() => {
                setView(value);
                setUnreadOnly(false);
              }}
            >
              {label}
            </Button>
          ))}
          <Button
            variant={unreadOnly ? 'primary' : 'outline'}
            size='sm'
            onClick={() => {
              setView('all');
              setUnreadOnly((current) => !current);
            }}
          >
            Unread ({counts.unread})
          </Button>
        </div>
        <div className='ih-notification-filters'>
          <Button
            variant='outline'
            size='sm'
            onClick={markAllRead}
            disabled={loading || markingAll || counts.unread === 0}
            locked={readOnly}
          >
            <Check size={13} /> Mark All as Read
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setRefreshVersion((current) => current + 1)}
            disabled={loading}
          >
            <RefreshCw size={13} /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className='ih-card ih-notification-state'>
          <span className='ih-spinner ih-notification-spinner' />
          Loading notifications…
        </div>
      ) : error ? (
        <div className='ih-card ih-notification-state'>
          <AlertCircle size={18} />
          <span>{error}</span>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setRefreshVersion((current) => current + 1)}
          >
            Try Again
          </Button>
        </div>
      ) : notifications.length === 0 ? (
        <div className='ih-card ih-notification-state'>
          <Bell size={20} />
          <strong>No notifications for this view</strong>
          <span>
            Screening and compliance updates appear here when in-app
            notifications are enabled.
          </span>
          <Button variant='outline' size='sm' onClick={onOpenSettings}>
            <Settings size={13} /> Notification Settings
          </Button>
        </div>
      ) : (
        <div className='ih-notification-list'>
          {notifications.map((notification) => {
            const meta = TYPE_META[notification.type] ?? {
              label: notification.system ? 'System' : 'User',
              cls: notification.system ? 'ih-badge-info' : 'ih-badge-muted',
            };
            return (
              <div
                key={notification.id}
                className={cx(
                  'ih-notif-item',
                  !notification.read && 'is-unread',
                )}
              >
                <span
                  className={cx(
                    'ih-notif-dot',
                    !notification.read && 'is-unread',
                  )}
                />
                <div className='ih-grow' style={{ minWidth: 0 }}>
                  <div className='ih-row-wrap ih-gap-2'>
                    <span className={cx('ih-badge', meta.cls)}>
                      {meta.label}
                    </span>
                    {notification.link ? (
                      <button
                        type='button'
                        className='ih-notification-link'
                        onClick={() => void openNotification(notification)}
                        disabled={busyId === notification.id}
                      >
                        {notification.heading}
                      </button>
                    ) : (
                      <div className='ih-text-sm ih-font-medium'>
                        {notification.heading}
                      </div>
                    )}
                  </div>
                  <p className='ih-text-xs ih-text-muted ih-mt-1'>
                    {notification.message}
                  </p>
                  <div className='ih-notification-meta'>
                    <span>{notification.sender || 'System'}</span>
                    <span>·</span>
                    <span>
                      {notification.createdAt
                        ? formatDate(notification.createdAt)
                        : 'Date unavailable'}
                    </span>
                  </div>
                </div>
                <div className='ih-notification-actions'>
                  {!notification.read && (
                    <Button
                      variant='ghost'
                      size='sm'
                      title='Mark as read'
                      locked={readOnly}
                      onClick={() => markRead(notification)}
                      disabled={busyId === notification.id}
                    >
                      <Check size={13} /> Read
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    size='icon'
                    title='Dismiss notification'
                    locked={readOnly}
                    onClick={(event) => dismiss(event, notification)}
                    disabled={busyId === notification.id}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && !error && total > pageSize && (
        <div className='ih-priority-pagination' aria-label='Notification pages'>
          <Button
            variant='outline'
            size='sm'
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <span className='ih-text-xs ih-text-muted ih-num'>
            Page {page} of {Math.ceil(total / pageSize)} · {total} notifications
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= Math.ceil(total / pageSize)}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function SubTab({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: IconComp;
  label: string;
}) {
  return (
    <button
      type='button'
      role='tab'
      aria-selected={active}
      data-active={active}
      onClick={onClick}
      className='ih-tab'
    >
      <Icon className='ih-database-tab-icon' size={16} /> {label}
    </button>
  );
}

/* ============================== Dashboard ============================== */
const formatExactCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

function Dashboard({
  products,
  summary,
  summaryLoading,
  tariffExposureData,
  tariffExposureLoading,
  pgaExposureData,
  pgaExposureLoading,
  tariffExposureError,
  pgaExposureError,
  onRetryExposure,
  databaseId,
  isRealDatabase,
  renderProductLink,
  onIssueFilter,
  priorityView,
  onPriorityViewChange,
  unreadNotificationCount,
  onUnreadNotificationCountChange,
  onOpenSettings,
}: {
  products: typeof IMPORT_PRODUCTS;
  summary: ImportProductListSummary | null;
  summaryLoading: boolean;
  tariffExposureData: ImportDatabaseTariffExposure | null;
  tariffExposureLoading: boolean;
  pgaExposureData: ImportDatabasePgaExposure | null;
  pgaExposureLoading: boolean;
  tariffExposureError: boolean;
  pgaExposureError: boolean;
  onRetryExposure: () => void;
  databaseId: string;
  isRealDatabase: boolean;
  renderProductLink: RenderProductLink;
  onIssueFilter: (issueType: string) => void;
  priorityView: PriorityView;
  onPriorityViewChange: (view: PriorityView) => void;
  unreadNotificationCount: number;
  onUnreadNotificationCountChange: (count: number) => void;
  onOpenSettings: () => void;
}) {
  const [priorityPage, setPriorityPage] = useState(1);
  const [issueSummary, setIssueSummary] =
    useState<ImportDatabaseIssueSummary | null>(null);
  const [issuesLoading, setIssuesLoading] = useState(isRealDatabase);
  const priorityPageSize = 10;
  const [showAllExposure, setShowAllExposure] = useState(false);
  const [showAllPga, setShowAllPga] = useState(false);

  useEffect(() => {
    setPriorityPage(1);
    setIssueSummary(null);
    setShowAllExposure(false);
    setShowAllPga(false);
  }, [databaseId, isRealDatabase]);

  useEffect(() => {
    if (!isRealDatabase) {
      setIssuesLoading(false);
      setIssueSummary(null);
      return;
    }

    // Priority Issues performs a separate database-wide normalization pass.
    // Let the summary request populate the KPI cards and rollups first instead
    // of making both operations compete during the initial dashboard load.
    if (summaryLoading) {
      setIssuesLoading(true);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setIssuesLoading(true);
    fetchImportDatabaseIssueSummary(
      databaseId,
      priorityPage,
      priorityPageSize,
      controller.signal,
    ).then(({ success, summary, error }) => {
      if (cancelled) return;
      setIssuesLoading(false);
      if (success && summary) {
        setIssueSummary(summary);
      } else {
        toast.error(error ?? 'Failed to load priority issues');
      }
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [databaseId, isRealDatabase, priorityPage, summaryLoading, summary]);

  const assessedProducts = products.filter(
    (product) => product.complianceAssessed !== false,
  );
  const total = (isRealDatabase ? summary?.total ?? 0 : products.length) || 1;
  const avgScore = isRealDatabase
    ? summary?.averageComplianceScore === null ||
      summary?.averageComplianceScore === undefined
      ? '—'
      : String(summary.averageComplianceScore)
    : assessedProducts.length > 0
    ? String(
        Math.round(
          assessedProducts.reduce(
            (sum, product) => sum + product.complianceScore,
            0,
          ) / assessedProducts.length,
        ),
      )
    : '—';
  const highRisk = isRealDatabase
    ? summary?.highRisk ?? 0
    : products.filter(
        (p) => p.riskLevel === 'High' || p.riskLevel === 'Critical',
      ).length;
  const flagged = isRealDatabase
    ? summary?.flagged ?? 0
    : products.filter(
        (p) =>
          p.pgaFlags.length > 0 ||
          p.section301 ||
          p.section232 ||
          p.uflpaRisk !== 'none' ||
          p.additionalDutyPct > 0,
      ).length;
  const HTS_RE = /^\d{4}\.\d{2}\.\d{2}$/;
  const fallbackMockIssues = products.filter(
    (p) => !p.htsCode || !HTS_RE.test(p.htsCode) || !p.countryOfOrigin,
  ).length;
  const issues = isRealDatabase ? summary?.totalIssues : fallbackMockIssues;
  // Keep dashboard totals aligned with the values users see on each product:
  // legacy's Product Value is the latest tariff snapshot's totalCost, while
  // Total Duty is the same snapshot's dutyCost. Mock rows have no snapshot
  // fields and retain their existing estimate as a fallback.
  const totalValue = isRealDatabase
    ? summary?.totalValue ?? 0
    : products.reduce(
        (totalValueSum, product) =>
          totalValueSum + (product.totalCost ?? product.declaredValue),
        0,
      );
  const baseDutyFor = (hts: string): number => {
    const ch = hts.slice(0, 2);
    const map: Record<string, number> = {
      '61': 16,
      '62': 16,
      '52': 8,
      '51': 7,
      '85': 0,
      '84': 2.5,
      '87': 2.5,
      '72': 0,
      '76': 3,
      '73': 3.7,
      '29': 0,
      '30': 0,
      '31': 0,
      '32': 6,
      '38': 3.7,
      '39': 4.2,
      '40': 4,
      '94': 3.2,
    };
    return map[ch] ?? 2.5;
  };
  const totalDuty = isRealDatabase
    ? summary?.totalDuty ?? 0
    : products.reduce((a, p) => {
        if (p.dutyCost !== undefined) return a + p.dutyCost;
        if (isRealDatabase) return a;
        const rate =
          baseDutyFor(p.htsCode) +
          (p.section301 ? p.section301Rate : 0) +
          (p.section232 ? p.section232Rate : 0) +
          p.additionalDutyPct;
        return a + (p.declaredValue * rate) / 100;
      }, 0);

  const riskDist = useMemo(() => {
    if (isRealDatabase && summary) return summary.riskDistribution;
    const m: Record<string, number> = {
      'Not Assessed': 0,
      'No Matches': 0,
      Low: 0,
      Medium: 0,
      High: 0,
      Critical: 0,
    };
    for (const p of products) m[p.riskLevel]++;
    return m;
  }, [isRealDatabase, products, summary]);

  const pgaRows = useMemo((): Array<{
    key: string;
    label: string;
    value: number;
  }> => {
    if (isRealDatabase) {
      return (pgaExposureData?.programs ?? [])
        .filter((program) => program.products > 0)
        .map((program) => ({
          key: program.code,
          label: program.label,
          value: program.products,
        }));
    }
    const m: Record<PgaAgency, number> = {
      FDA: 0,
      EPA: 0,
      USDA: 0,
      FWS: 0,
      CPSC: 0,
      TSCA: 0,
      DOT: 0,
    };
    for (const p of products) for (const a of p.pgaFlags) m[a]++;
    return PGA_AGENCIES.map((agency) => ({
      key: agency,
      label: agency,
      value: m[agency] ?? 0,
    }));
  }, [isRealDatabase, products, pgaExposureData]);
  const complianceRows = useMemo(
    (): Array<{ key: string; label: string; value: number }> => [
      {
        key: 'forced-labor',
        label: 'Forced Labor',
        value: isRealDatabase
          ? pgaExposureData?.forcedLaborProducts ?? 0
          : products.filter((p) => p.uflpaRisk !== 'none').length,
      },
      {
        key: 'adcvd',
        label: 'AD/CVD',
        value: isRealDatabase
          ? pgaExposureData?.adcvdProducts ?? 0
          : products.filter((p) => (p.adcvdCases?.length ?? 0) > 0).length,
      },
    ],
    [isRealDatabase, products, pgaExposureData],
  );
  // One scale across both groups so PGA and compliance bars compare directly.
  const pgaMax = Math.max(
    1,
    ...pgaRows.map((row) => row.value),
    ...complianceRows.map((row) => row.value),
  );
  const visiblePgaRows = showAllPga
    ? pgaRows
    : pgaRows.slice(0, EXPOSURE_ROW_LIMIT);

  const tariffExposure = useMemo((): {
    rows: Array<{ key: string; label: string; value: number; color: string }>;
    total: number;
  } => {
    if (isRealDatabase) {
      if (!tariffExposureData) return { rows: [], total: 0 };
      const rows = [
        {
          key: 'base_duty',
          label: 'Base duty',
          value: tariffExposureData.baseDuty.duty,
          color: 'var(--ih-tariff-base)',
        },
        ...tariffExposureData.programs.map((program, index) => ({
          key: program.bucket,
          label: program.label,
          value: program.duty,
          color: `var(--ih-tariff-${(index % TARIFF_PROGRAM_COLOR_COUNT) + 1})`,
        })),
        {
          key: 'other_additional',
          label: 'Other additional duty',
          value: tariffExposureData.otherAdditional.duty,
          color: 'var(--ih-tariff-other)',
        },
      ].filter((row) => row.value >= 0.005);
      return { rows, total: tariffExposureData.totalDuty };
    }
    let v301 = 0,
      v232 = 0,
      vAdd = 0;
    for (const p of products) {
      v301 +=
        p.section301DutyCost ??
        (!isRealDatabase && p.section301
          ? p.declaredValue * (p.section301Rate / 100)
          : 0);
      v232 +=
        p.section232DutyCost ??
        (!isRealDatabase && p.section232
          ? p.declaredValue * (p.section232Rate / 100)
          : 0);
      vAdd +=
        p.otherAdditionalDutyCost ??
        (!isRealDatabase ? p.declaredValue * (p.additionalDutyPct / 100) : 0);
    }
    return {
      rows: [
        {
          key: 'sec_301',
          label: 'Section 301',
          value: v301,
          color: 'var(--ih-tariff-1)',
        },
        {
          key: 'sec_232',
          label: 'Section 232',
          value: v232,
          color: 'var(--ih-tariff-2)',
        },
        {
          key: 'other_additional',
          label: 'Additional duty',
          value: vAdd,
          color: 'var(--ih-tariff-other)',
        },
      ],
      total: v301 + v232 + vAdd,
    };
  }, [isRealDatabase, products, tariffExposureData]);
  const visibleExposureRows = showAllExposure
    ? tariffExposure.rows
    : tariffExposure.rows.slice(0, EXPOSURE_ROW_LIMIT);

  // Real products store country_of_origin as an ISO-2 code (e.g. "CN"), but
  // WorldMap matches by full country name — normalize before aggregating so
  // the map actually colors real databases, not just mock ones (which already
  // use full names).
  const countryCounts = useMemo(() => {
    const m: Record<string, number> = {};
    if (isRealDatabase && summary) {
      Object.entries(summary.countryCounts).forEach(([country, count]) => {
        const name = countryNameFromCode(country);
        m[name] = (m[name] ?? 0) + count;
      });
      return m;
    }
    for (const p of products) {
      if (!p.countryOfOrigin) continue;
      const name = countryNameFromCode(p.countryOfOrigin);
      m[name] = (m[name] ?? 0) + 1;
    }
    return m;
  }, [isRealDatabase, products, summary]);

  return (
    <div className='ih-col ih-gap-4'>
      <div className='ih-kpi-cluster'>
        <KpiTile
          loading={summaryLoading}
          icon={Package}
          label='Total Products'
          value={
            summaryLoading
              ? '—'
              : String(isRealDatabase ? summary?.total ?? 0 : products.length)
          }
        />
        <KpiTile
          loading={summaryLoading}
          icon={Activity}
          label='Avg Score'
          value={summaryLoading ? '—' : avgScore}
        />
        <KpiTile
          loading={summaryLoading}
          icon={AlertTriangle}
          label='High Risk'
          value={summaryLoading ? '—' : String(highRisk)}
          tone='danger'
        />
        <KpiTile
          loading={summaryLoading}
          icon={Flag}
          label='Total Flagged'
          value={summaryLoading ? '—' : String(flagged)}
          tone='warning'
        />
        <KpiTile
          loading={summaryLoading}
          icon={AlertCircle}
          label='Total Issues'
          value={summaryLoading || issues == null ? '—' : String(issues)}
          tone={issues != null && issues > 0 ? 'danger' : undefined}
        />
        <KpiTile
          loading={summaryLoading}
          icon={DollarSign}
          label='Total Value'
          value={summaryLoading ? '—' : formatExactCurrency(totalValue)}
        />
        <KpiTile
          loading={summaryLoading}
          icon={Receipt}
          label='Total Duty'
          value={summaryLoading ? '—' : formatExactCurrency(totalDuty)}
        />
      </div>

      <div className='ih-dashboard-3col'>
        <div className='ih-card'>
          <h2
            className='ih-font-semibold ih-text-sm'
            style={{ marginBottom: 12 }}
          >
            Risk distribution
          </h2>
          <DashboardCardBody loading={summaryLoading}>
            <div className='ih-col ih-gap-2'>
              {(
                [
                  'Not Assessed',
                  'No Matches',
                  'Low',
                  'Medium',
                  'High',
                  'Critical',
                ] as const
              ).map((r) => {
                const n = riskDist[r] ?? 0;
                const pct = (n / total) * 100;
                const color =
                  r === 'Critical'
                    ? 'var(--danger)'
                    : r === 'High'
                    ? 'var(--warning)'
                    : r === 'Medium'
                    ? 'var(--info)'
                    : r === 'Low'
                    ? 'var(--success)'
                    : r === 'No Matches'
                    ? 'var(--ih-tariff-6)'
                    : 'var(--text-secondary)';
                return (
                  <BarRow key={r} label={r} value={n} pct={pct} color={color} />
                );
              })}
            </div>
          </DashboardCardBody>
        </div>

        <div className='ih-card'>
          <h2
            className='ih-font-semibold ih-text-sm'
            style={{ marginBottom: 12 }}
          >
            Agency &amp; compliance exposure
          </h2>
          <DashboardCardBody
            loading={summaryLoading || (isRealDatabase && pgaExposureLoading)}
            error={
              isRealDatabase && pgaExposureError
                ? "Couldn't load agency & compliance exposure."
                : undefined
            }
            onRetry={onRetryExposure}
          >
            <div className='ih-col ih-gap-2'>
              {pgaRows.length === 0 && (
                <div className='ih-text-xs ih-text-muted'>
                  No PGA hits found. Run screening to re-check agency exposure.
                </div>
              )}
              {visiblePgaRows.map((row) => (
                <BarRow
                  key={row.key}
                  label={row.label}
                  value={row.value}
                  pct={(row.value / pgaMax) * 100}
                  color='var(--action-color)'
                />
              ))}
              {pgaRows.length > EXPOSURE_ROW_LIMIT && (
                <button
                  type='button'
                  className='ih-btn-link ih-text-xs ih-exposure-toggle'
                  onClick={() => setShowAllPga((value) => !value)}
                >
                  {showAllPga ? 'Show less' : `Show all (${pgaRows.length})`}
                </button>
              )}
              <div className='ih-exposure-divider' />
              {complianceRows.map((row) => (
                <BarRow
                  key={row.key}
                  label={row.label}
                  value={row.value}
                  pct={(row.value / pgaMax) * 100}
                  color='var(--ih-compliance-meter)'
                />
              ))}
            </div>
          </DashboardCardBody>
        </div>

        <div className='ih-card'>
          <h2
            className='ih-font-semibold ih-text-sm'
            style={{ marginBottom: 12 }}
          >
            Tariff exposure
          </h2>
          <DashboardCardBody
            loading={
              summaryLoading || (isRealDatabase && tariffExposureLoading)
            }
            error={
              isRealDatabase && tariffExposureError
                ? "Couldn't load tariff exposure."
                : undefined
            }
            onRetry={onRetryExposure}
          >
            <div className='ih-col ih-gap-3'>
              {tariffExposure.rows.length === 0 && (
                <div className='ih-text-xs ih-text-muted'>
                  No duty calculated. Run screening to see most updated tariff
                  exposure.
                </div>
              )}
              {visibleExposureRows.map((row) => (
                <ExposureRow
                  key={row.key}
                  label={row.label}
                  value={row.value}
                  total={tariffExposure.total}
                  color={row.color}
                />
              ))}
              {tariffExposure.rows.length > EXPOSURE_ROW_LIMIT && (
                <button
                  type='button'
                  className='ih-btn-link ih-text-xs ih-exposure-toggle'
                  onClick={() => setShowAllExposure((value) => !value)}
                >
                  {showAllExposure
                    ? 'Show less'
                    : `Show all (${tariffExposure.rows.length})`}
                </button>
              )}
              <div className='ih-exposure-total'>
                <span className='ih-text-muted'>Total exposure</span>
                <span className='ih-font-semibold ih-num'>
                  {formatExactCurrency(tariffExposure.total)}
                </span>
              </div>
            </div>
          </DashboardCardBody>
        </div>
      </div>

      <div className='ih-dashboard-2col'>
        <PriorityIssuesCard
          summary={issueSummary}
          loading={issuesLoading}
          page={priorityPage}
          onPageChange={setPriorityPage}
          renderProductLink={renderProductLink}
          onIssueFilter={onIssueFilter}
          view={priorityView}
          onViewChange={onPriorityViewChange}
          unreadNotificationCount={unreadNotificationCount}
          notifications={
            <DbNotifications
              databaseId={databaseId}
              isRealDatabase={isRealDatabase}
              onUnreadCountChange={onUnreadNotificationCountChange}
              onOpenSettings={onOpenSettings}
            />
          }
        />
        <div className='ih-card ih-dashboard-map-card'>
          <h2 className='ih-font-semibold ih-text-sm'>Countries of origin</h2>
          <p className='ih-text-xs ih-text-muted ih-mt-1'>
            Distribution of product origin
          </p>
          <div className='ih-dashboard-map'>
            <WorldMap
              countryCounts={countryCounts}
              onCountryClick={() => undefined}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// A hidden copy of the tallest collapsed card (Tariff exposure: the row limit,
// the "Show all" link and the total) sits under the content in the same grid
// cell, so dashboard cards keep one height while loading and once loaded.
function DashboardCardBody({
  loading,
  error,
  onRetry,
  children,
}: {
  loading: boolean;
  error?: string;
  onRetry?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className='ih-dashboard-card-body'>
      <div className='ih-col ih-gap-3 ih-dashboard-card-reserve' aria-hidden>
        {Array.from({ length: EXPOSURE_ROW_LIMIT }, (_, index) => (
          <BarRow key={index} label='—' value={0} pct={0} color='transparent' />
        ))}
        <button
          type='button'
          tabIndex={-1}
          className='ih-btn-link ih-text-xs ih-exposure-toggle'
        >
          Show all
        </button>
        <div className='ih-exposure-total'>
          <span className='ih-font-semibold ih-num'>—</span>
        </div>
      </div>
      {loading ? (
        <DashboardSummaryLoading />
      ) : error ? (
        <div className='ih-col ih-gap-2 ih-text-xs ih-text-muted' role='alert'>
          {error}
          {onRetry && (
            <button
              type='button'
              className='ih-btn-link ih-text-xs ih-exposure-toggle'
              onClick={onRetry}
            >
              Retry
            </button>
          )}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function DashboardSummaryLoading() {
  return (
    <div
      className='ih-row ih-gap-2 ih-text-xs ih-text-muted'
      style={{ alignItems: 'center', minHeight: 72 }}
    >
      <span
        className='ih-spinner'
        style={{ width: 14, height: 14, borderWidth: 2 }}
      />
      Loading summary…
    </div>
  );
}

const issueTypeLabel = (type: string): string =>
  ({
    hts_review_required: 'HTS review',
    tariff_change: 'Tariff changed',
    analysis_required: 'Analysis required',
    compliance_flag: 'Compliance flags',
  }[type] ?? type);

function PriorityIssuesCard({
  summary,
  loading,
  page,
  onPageChange,
  renderProductLink,
  onIssueFilter,
  view,
  onViewChange,
  unreadNotificationCount,
  notifications,
}: {
  summary: ImportDatabaseIssueSummary | null;
  loading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  renderProductLink: RenderProductLink;
  onIssueFilter: (issueType: string) => void;
  view: PriorityView;
  onViewChange: (view: PriorityView) => void;
  unreadNotificationCount: number;
  notifications: React.ReactNode;
}) {
  const issues = summary?.priorityIssues ?? [];
  const pageCount = Math.max(
    1,
    Math.ceil((summary?.priorityTotal ?? 0) / (summary?.pageSize ?? 10)),
  );

  return (
    <div className='ih-card ih-priority-card'>
      <div className='ih-priority-header'>
        <div>
          <h2 className='ih-font-semibold ih-text-sm'>Priority Issues</h2>
          <p className='ih-text-xs ih-text-muted ih-mt-1'>
            {view === 'issues'
              ? 'Top issues requiring attention'
              : 'Screening and compliance updates for this database'}
          </p>
        </div>
        <div className='ih-row ih-gap-2'>
          {view === 'issues' && loading && (
            <span className='ih-spinner ih-priority-spinner' />
          )}
          <div
            className='ih-segmented'
            role='group'
            aria-label='Priority Issues view'
          >
            {(
              [
                ['notifications', `Notifications (${unreadNotificationCount})`],
                ['issues', 'Issues'],
              ] as Array<[PriorityView, string]>
            ).map(([value, label]) => (
              <button
                type='button'
                key={value}
                className={cx(
                  'ih-segmented-item',
                  view === value && 'is-active',
                )}
                aria-pressed={view === value}
                onClick={() => onViewChange(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'notifications' ? (
        <div className='ih-priority-notifications'>{notifications}</div>
      ) : !loading && issues.length === 0 ? (
        <div className='ih-priority-empty'>No Priority Issues Found</div>
      ) : (
        <>
          {!!summary && Object.keys(summary.issueCounts).length > 0 && (
            <div className='ih-priority-summary-filters'>
              {Object.entries(summary.issueCounts).map(([type, count]) => (
                <button
                  type='button'
                  key={type}
                  onClick={() => onIssueFilter(type)}
                  className='ih-priority-summary-filter'
                >
                  <span>{issueTypeLabel(type)}</span>
                  <strong>{count}</strong>
                </button>
              ))}
            </div>
          )}
          <div className='ih-priority-list'>
            {issues.map((issue) => (
              <React.Fragment key={issue.id}>
                {renderProductLink({
                  id: issue.id,
                  className: 'ih-priority-item',
                  children: (
                    <>
                      <div className='ih-priority-item-title'>
                        <span>{issue.name}</span>
                        <span className='ih-priority-chips'>
                          {issue.normalizedIssues.map((normalizedIssue) => (
                            <span
                              key={normalizedIssue.id}
                              className={cx(
                                'ih-badge',
                                normalizedIssue.severity === 'danger'
                                  ? 'ih-badge-danger'
                                  : normalizedIssue.severity === 'warning'
                                  ? 'ih-badge-warning'
                                  : 'ih-badge-info',
                              )}
                            >
                              {normalizedIssue.title}
                            </span>
                          ))}
                        </span>
                      </div>
                      <div className='ih-priority-item-detail'>
                        <span className='ih-font-medium'>
                          Compliance Score:
                        </span>{' '}
                        <span className='ih-num'>
                          {issue.totalComplianceScore}%
                        </span>
                        <span className='ih-font-medium'>Description:</span>{' '}
                        <span>{issue.description || '—'}</span>
                      </div>
                    </>
                  ),
                })}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {view === 'issues' && (summary?.priorityTotal ?? 0) > 0 && (
        <div className='ih-priority-pagination'>
          <span className='ih-text-xs ih-text-muted ih-num'>
            Page {page} of {pageCount}
          </span>
          <div className='ih-row ih-gap-2'>
            <Button
              variant='outline'
              size='sm'
              disabled={loading || page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Previous
            </Button>
            <Button
              variant='outline'
              size='sm'
              disabled={loading || page >= pageCount}
              onClick={() => onPageChange(Math.min(pageCount, page + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function BarRow({
  label,
  labelClass,
  value,
  pct,
  color,
}: {
  label: string;
  labelClass?: string;
  value: number;
  pct: number;
  color: string;
}) {
  return (
    <div>
      <div className='ih-bar-row-head'>
        <span className={labelClass}>{label}</span>
        <span className='ih-text-muted ih-num'>{value}</span>
      </div>
      <div className='ih-bar-track'>
        <div
          className='ih-bar-fill'
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

// Rows shown before "Show all", and the number of --ih-tariff-N colour tokens
// defined in importHub.css (programs beyond that reuse colours in order).
const EXPOSURE_ROW_LIMIT = 6;
const TARIFF_PROGRAM_COLOR_COUNT = 12;

function ExposureRow({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className='ih-bar-row-head'>
        <span>{label}</span>
        <span className='ih-text-muted ih-num'>
          {formatExactCurrency(value)}
        </span>
      </div>
      <div className='ih-bar-track'>
        <div
          className='ih-bar-fill'
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

/* ============================== Products tab ============================== */
const ProductsTab: React.FC<{
  products: typeof IMPORT_PRODUCTS;
  totalProducts: number;
  page: number;
  pageSize: number;
  loading: boolean;
  facets: {
    origins: string[];
    databases: Array<{ id: string; name: string }>;
  };
  dbId: string;
  databaseStructure: 'hierarchy' | 'flat' | 'hts-code-grouping';
  initialFilters?: ImportDatabaseFilters;
  onFiltersChange?: (filters: ImportDatabaseFilters) => void;
  renderProductLink: RenderProductLink;
  /** False for mock databases — the Run Screening button/banner only render for real ones (Slice 10). */
  isRealDb: boolean;
  screeningStatus: ImportScreeningStatus | null;
  screeningActive: boolean;
  onRunScreening: () => void;
  onRunAiEvaluation: () => void;
  onProductsChanged: () => void;
  onExportProducts: (productIds: string[]) => void;
  onPageChange: (page: number) => void;
  onServerQueryChange: (query: ImportTableServerQuery) => void;
}> = ({
  products,
  totalProducts,
  page,
  pageSize,
  loading,
  facets,
  dbId,
  databaseStructure,
  initialFilters,
  onFiltersChange,
  renderProductLink,
  isRealDb,
  screeningStatus,
  screeningActive,
  onRunScreening,
  onRunAiEvaluation,
  onProductsChanged,
  onExportProducts,
  onPageChange,
  onServerQueryChange,
}) => {
  const readOnly = useImportHubReadOnly();
  // Keep rows locked through AI/compliance finalization, not only through the
  // core OpenSearch phase. The final product/dashboard refetch is triggered
  // after this same overall status becomes terminal.
  const screeningOverlayActive = screeningActive;
  // Which toolbar button shows the running state.
  const aiRunActive = screeningActive && screeningStatus?.aiRequested === true;
  const initial: ImportTableFilters = {
    q: initialFilters?.q ?? '',
    risk: initialFilters?.risk ?? [],
    pga: initialFilters?.pga ?? [],
    tariff: initialFilters?.tariff ?? [],
    value: initialFilters?.value ?? [],
    fta: initialFilters?.fta ?? [],
    origin: initialFilters?.origin ?? [],
    addDuty: initialFilters?.addDuty ?? [],
    score: initialFilters?.score ?? [],
    classification: initialFilters?.classification ?? [],
    issues: initialFilters?.issues ?? [],
    hierarchy: initialFilters?.hierarchy ?? [],
    ruleset: initialFilters?.ruleset ?? [],
  };
  return (
    <div className='ih-products-tab'>
      {/* Screening progress overlays the products table (like the legacy
          Products Database tool's screening-overlay over MasterTable),
          rather than a separate banner above it — a centered "Screen in
          Progress…" message dims/covers the table so the user has a clear,
          unmissable signal while a run is active. pointer-events:none on the
          table itself is defense-in-depth (the overlay's own pointer-events
          already blocks clicks once visible); rows must not be clickable
          while their data is mid-update. */}
      <div style={{ position: 'relative' }}>
        <div
          style={
            screeningOverlayActive
              ? { pointerEvents: 'none', userSelect: 'none' }
              : undefined
          }
          aria-disabled={screeningOverlayActive || undefined}
        >
          <ImportProductsTable
            products={products}
            serverSide={isRealDb}
            totalProducts={totalProducts}
            page={page}
            pageSize={pageSize}
            loading={loading}
            facets={facets}
            onPageChange={onPageChange}
            onServerQueryChange={onServerQueryChange}
            savedViewDatabaseId={dbId}
            databaseStructure={databaseStructure}
            initialFilters={initial}
            renderProductLink={renderProductLink}
            onProductsChanged={onProductsChanged}
            onExportProducts={onExportProducts}
            toolbarAction={
              isRealDb ? (
                <div className='flex items-center gap-2'>
                  <button
                    type='button'
                    className='ih-products-screening-button'
                    title={readOnly ? 'Requires a Business license' : undefined}
                    onClick={onRunScreening}
                    disabled={screeningActive || readOnly}
                  >
                    {screeningActive && !aiRunActive ? (
                      <>
                        <span
                          className='ih-spinner'
                          style={{ width: 14, height: 14, borderWidth: 2 }}
                        />
                        Screening…
                      </>
                    ) : readOnly ? (
                      <>
                        <Lock size={18} /> Run Screening
                      </>
                    ) : (
                      <>
                        <Sparkles size={18} /> Run Screening
                      </>
                    )}
                  </button>
                  <button
                    type='button'
                    className='ih-products-screening-button'
                    title={
                      readOnly
                        ? 'Requires a Business license'
                        : 'Screen every product and run the AI evaluation for each'
                    }
                    onClick={onRunAiEvaluation}
                    disabled={screeningActive || readOnly}
                  >
                    {aiRunActive ? (
                      <>
                        <span
                          className='ih-spinner'
                          style={{ width: 14, height: 14, borderWidth: 2 }}
                        />
                        Evaluating…
                      </>
                    ) : readOnly ? (
                      <>
                        <Lock size={18} /> Run AI Evaluation
                      </>
                    ) : (
                      <>
                        <Bot size={18} /> Run AI Evaluation
                      </>
                    )}
                  </button>
                </div>
              ) : undefined
            }
            onFiltersChange={(f) => {
              onFiltersChange?.({
                q: f.q ?? '',
                risk: f.risk ?? [],
                pga: f.pga ?? [],
                tariff: f.tariff ?? [],
                value: f.value ?? [],
                fta: f.fta ?? [],
                origin: f.origin ?? [],
                addDuty: f.addDuty ?? [],
                score: f.score ?? [],
                classification: f.classification ?? [],
                issues: f.issues ?? [],
                hierarchy: f.hierarchy ?? [],
                ruleset: f.ruleset ?? [],
              });
            }}
          />
        </div>
        {isRealDb && (
          <ScreeningOverlay
            status={screeningStatus}
            active={screeningOverlayActive}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Whole-database screening progress (Slice 10), overlaid on the products
 * table — mirrors the legacy Products Database tool's screening-overlay over
 * MasterTable (centered "Screen in Progress…" message on a dark scrim,
 * cross-faded in/out) rather than a banner above the table.
 */
const clampPercent = (value: number | null): number | null =>
  value == null ? null : Math.max(0, Math.min(100, value));

const formatCount = (value: number) => value.toLocaleString();

const formatElapsed = (
  startedAt: string | null,
  now: number,
): string | null => {
  if (!startedAt) return null;
  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) return null;
  const seconds = Math.max(0, Math.round((now - started) / 1000));
  if (seconds < 60) return `${seconds}s elapsed`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m elapsed`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m elapsed`;
};

/**
 * Resolve the run's phase and a determinate progress figure from the live
 * counters.
 *
 * `aiInProgress` is deliberately only a fallback. It is a single run-level flag
 * that the *first* chunk to reach the AI stage sets, so on a large database it
 * turns true almost immediately and stays true — driving the label from it made
 * the overlay read "Analyzing Results…" for an entire hour-long run, skipping the
 * screening phase entirely. The chunk/product counters describe the two stages
 * separately, so they are what the UI reports.
 */
function resolveScreeningPhase(status: ImportScreeningStatus | null): {
  title: string;
  percent: number | null;
  progressLabel: string | null;
} {
  if (!status) {
    return {
      title: 'Screening in Progress…',
      percent: null,
      progressLabel: null,
    };
  }

  const { chunkTotal, publishedChunks, totalProducts } = status;
  const useProducts = totalProducts != null && totalProducts > 0;
  const unitTotal = useProducts ? (totalProducts as number) : chunkTotal;
  const noun = useProducts ? 'products' : 'chunks';
  const screenedCount = useProducts
    ? status.productsProcessed
    : status.chunkProcessed;
  const analyzedCount = useProducts
    ? status.postProductsProcessed
    : status.postChunkProcessed;

  const countLabel = (done: number | null): string | null =>
    done != null && unitTotal != null && unitTotal > 0
      ? `${formatCount(done)} of ${formatCount(unitTotal)} ${noun}`
      : null;

  if (status.starting) {
    return { title: 'Starting screening…', percent: 0, progressLabel: null };
  }

  if (status.finalizing) {
    return {
      title: 'Finalizing screening…',
      percent: 100,
      progressLabel: null,
    };
  }

  // Still handing work to the queue. Reported separately so a large database does
  // not appear stalled at 0% while the server is legitimately busy queueing.
  if (
    chunkTotal != null &&
    chunkTotal > 0 &&
    publishedChunks != null &&
    publishedChunks < chunkTotal
  ) {
    return {
      title: 'Queueing screening…',
      percent: clampPercent(Math.round((publishedChunks / chunkTotal) * 100)),
      progressLabel: useProducts
        ? `Preparing ${formatCount(totalProducts as number)} products`
        : null,
    };
  }

  const screeningDone =
    unitTotal != null &&
    unitTotal > 0 &&
    screenedCount != null &&
    screenedCount >= unitTotal;

  if (
    !screeningDone &&
    (status.screeningInProgress || status.aiRequested === false)
  ) {
    return {
      title: 'Screening products…',
      percent: clampPercent(status.percent),
      progressLabel: countLabel(screenedCount),
    };
  }

  // Plain screening ends after the core compliance/tariff stage. Only Analyze
  // Product and Run AI Evaluation request AI, so the overlay must never imply an
  // analysis is running while the coordinator finishes persistence and
  // refreshes the page data.
  if (status.aiRequested === false) {
    return {
      title: 'Finalizing screening…',
      percent: 100,
      progressLabel: countLabel(screenedCount),
    };
  }

  if (status.verificationInProgress) {
    return {
      title: 'Verifying results…',
      percent: clampPercent(status.postPercent ?? status.percent),
      progressLabel: countLabel(analyzedCount),
    };
  }

  return {
    title: 'Analyzing results…',
    percent: clampPercent(status.postPercent),
    progressLabel: countLabel(analyzedCount),
  };
}

function ScreeningOverlay({
  status,
  active,
}: {
  status: ImportScreeningStatus | null;
  active: boolean;
}) {
  // Ticks the elapsed-time readout. A long run with a frozen timer reads as a
  // hung page.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [active]);

  const { title, percent, progressLabel } = resolveScreeningPhase(status);
  const indeterminateProgress = active && percent == null;
  const elapsed = formatElapsed(status?.startedAt ?? null, now);
  const isLargeRun = (status?.totalProducts ?? 0) >= 1000;
  const detail = status?.finalizing
    ? 'Updating compliance scores and dashboard totals.'
    : isLargeRun
    ? 'Large databases can take a while — you can leave this page and come back.'
    : 'This could take a few minutes.';

  return (
    <div className={cx('ih-screening-overlay', active && 'is-visible')}>
      <div className='ih-screening-message'>
        <span
          className='ih-spinner'
          style={{ width: 22, height: 22, borderWidth: 3 }}
        />
        <h3>{title}</h3>
        <p>{detail}</p>
        <div
          className='ih-screening-progress'
          role='progressbar'
          aria-label='Database screening progress'
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent ?? undefined}
          aria-valuetext={
            indeterminateProgress ? `${title} in progress` : undefined
          }
        >
          <div
            className={cx(
              'ih-screening-progress-fill',
              indeterminateProgress && 'is-indeterminate',
            )}
            style={
              indeterminateProgress ? undefined : { width: `${percent ?? 0}%` }
            }
          />
        </div>
        <p className='ih-screening-progress-meta' aria-live='polite'>
          {[
            percent != null ? `${percent}%` : 'Calculating progress…',
            progressLabel,
            elapsed,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    </div>
  );
}
